import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { offerClosedStatusText } from '../OfferManagement/utils';
import NegotiationAPI from '../../agents/negotiationAgent';
import DocumentAPI from '../../agents/documentAgent';
import { sendDocumentEnvelope } from '../../agents/docusignAgent';
import useNegotiation from '../../agents/useNegotiation';
import DocumentFlowPanel from '../DocumentFlowPanel/DocumentFlowPanel';
import SendDocumentDialog from '../SendDocumentDialog/SendDocumentDialog';
import MaritalAttestationCard from '../MaritalAttestationCard/MaritalAttestationCard';
import LeadPaintDisclosureCard from '../LeadPaintDisclosureCard/LeadPaintDisclosureCard';
import { useSupportedStates } from '../../agents/supportedStatesAgent';
import { useAuth } from '../../agents/authAgent';
import { toDateOnly, formatDateOnly } from '../OfferManagement/utils';
import {
  deadlineTimeLabel,
  formatDeadline,
  getDefaultExpiresOn,
  getExpirationBounds,
  getExpirationError,
} from '../OfferManagement/offerExpiration';
import AcceptOfferModal from '../OfferManagement/modals/AcceptOfferModal';
import {
  CONTINGENCIES_UNCHANGED_NOTE,
  CONTINGENCY_UNAVAILABLE_MESSAGE,
  CURE_COPY,
  CURE_ELECTION_FIELD,
  FINANCING_KEY,
  FINANCING_TERMS_COPY,
  FINANCING_TERMS_FIELD,
  TITLE_PERIODS_COPY,
  TITLE_PERIODS_FIELD,
  cureElectionAsked,
  cureElectionCategory,
  cureElectionChanged,
  cureElectionFromOffer,
  deadlineKey,
  financingLoanAmount,
  financingTermsAsked,
  financingTermsChanged,
  financingTermsFieldErrors,
  financingTermsFromOffer,
  getContingencyLabelWithDeadline,
  getCureElectionSummary,
  getContingencyOptionsForOffer,
  getFinancingTermsSummary,
  getSelectedContingencyKeys,
  getTitlePeriodsSummary,
  isWisconsinHomeOffer,
  leadPaintElectionFromOffer,
  leadPaintElectionMode,
  normalizeContingencies,
  normalizeContingencyDeadlines,
  offerElectionError,
  offerTermsCategory,
  parseContingencyElectionError,
  serializeContingencies,
  titlePeriodsChanged,
  titlePeriodsFromOffer,
  unavailableSelectedKeys,
  withoutFinancingTerms,
} from '../OfferManagement/contingencyCatalog';
import { ContingencyErrorBanner, ContingencyFieldError } from '../OfferManagement/ContingencyElectionErrors';
import ContingencyDeadlineField from '../OfferManagement/ContingencyDeadlineField';
import LandContingencyNote, { ContingencyDeadlineNote } from '../OfferManagement/LandContingencyNote';
import OfferDeadlineDisclaimer from '../OfferManagement/OfferDeadlineDisclaimer';
import LeadPaintElectionField, { LeadPaintElectionCarried } from '../OfferManagement/LeadPaintElectionField';
import CureElectionField from '../OfferManagement/CureElectionField';
import FinancingTermsField from '../OfferManagement/FinancingTermsField';
import TitlePeriodsField from '../OfferManagement/TitlePeriodsField';
import { priceDeviation, isCounterPriceOutOfRange } from '../../utils/counterPrice';
import {
  counterEarnestError,
  counterEarnestPayload,
  initialCounterEarnest,
} from '../../utils/counterEarnest';
import TransactionPhaseBar from './TransactionPhaseBar';
import DocumentGateSection from './DocumentGateSection';
import WorkspacePaperworkRow from './WorkspacePaperworkRow';
import {
  WORKSPACE_DISCLOSURE_COPY as DISCLOSURE_COPY,
  workspaceDisclosureBasis,
  workspaceDisclosureTitle,
} from './disclosureCopy';
import './NegotiationWorkspace.css';

// QA F8: shown when another offer on the listing already holds a live accepted deal.
const PROPERTY_UNDER_CONTRACT_TEXT =
  "This home is already under contract with another buyer. You can't accept another offer while that deal is in progress.";

const WORKSPACES_PER_PAGE = 9;

// Shown by the agreement CTAs while a Wisconsin deal still needs a party's
// marital-status confirmation (the backend refuses to send until both have).
export const MARITAL_ATTESTATION_PENDING_NOTE =
  'Both buyer and seller must confirm their marital status before the agreement can be sent.';

/**
 * Pick the "active" purchase agreement from a document list and determine
 * whether it can be reused (sent envelope) or a new one must be generated.
 *
 * Logic (extracted from openEnvelopeWorkspace for testability):
 *   1. Filter to purchase_agreement documents.
 *   2. Sort: envelope-bearing rows first, then created_at desc.
 *      Prevents a stale draft PA from shadowing a server-provisioned envelope
 *      (F3: server-side provisioning task races with the browser flow).
 *   3. `existingIsSendable` requires BOTH an active signing status
 *      (sent/viewed/signed/completed) AND a real `docusign_envelope_id`.
 *      A document in `sent` status without an envelope_id is a phantom row
 *      (legacy / mid-server-task) and must not be reused — opening
 *      DocumentFlowPanel against it would fail.
 *
 * @param {Array<Object>} documents - raw document list from listDocuments.
 * @returns {{ purchaseAgreement: Object|null, existingIsSendable: boolean }}
 */
export const pickActivePurchaseAgreement = (documents) => {
  const purchaseAgreements = (Array.isArray(documents) ? documents : [])
    .filter((document) => document?.document_type === 'purchase_agreement');
  const sortedPAs = purchaseAgreements.sort((a, b) => {
    const aHasEnvelope = Boolean(a?.docusign_envelope_id);
    const bHasEnvelope = Boolean(b?.docusign_envelope_id);
    if (aHasEnvelope !== bHasEnvelope) return aHasEnvelope ? -1 : 1;
    return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
  });
  const purchaseAgreement = sortedPAs[0] ?? null;
  const existingIsSendable = Boolean(
    purchaseAgreement
      && ['sent', 'viewed', 'signed', 'completed'].includes(
        (purchaseAgreement.status || '').toLowerCase(),
      )
      && Boolean(purchaseAgreement.docusign_envelope_id),
  );
  return { purchaseAgreement, existingIsSendable };
};

/**
 * Format a price as USD currency string.
 */
const formatPrice = (price) => {
  if (!price && price !== 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(price);
};

/**
 * Whole-day difference between two `YYYY-MM-DD` date-only strings
 * (`next - current`), or 0 when either is missing or unparseable. Parsed at
 * UTC midnight so a day count never shifts by one across a timezone boundary.
 */
const dateOnlyDayDiff = (nextDate, currentDate) => {
  if (!nextDate || !currentDate) return 0;
  const a = Date.parse(`${nextDate}T00:00:00Z`);
  const b = Date.parse(`${currentDate}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((a - b) / 86400000);
};

/**
 * Plain-language description of how a counter's terms differ from the offer
 * currently on the table — price, earnest, and closing date, the terms a party
 * weighs first. Returns an array of change phrases (empty when nothing material
 * changed) for the live "vs. current offer" hint in CounterOfferForm. Direction
 * words stay neutral ("higher" / "earlier") because whether a change helps
 * depends on which side of the deal you are on. A blank/absent earnest means
 * "keep the current deposit", so it is not a change.
 */
const describeCounterDelta = (nextTerms = {}, currentTerms = {}) => {
  const parts = [];

  const nextPrice = Number(nextTerms.offer_price) || 0;
  const curPrice = Number(currentTerms.offer_price) || 0;
  if (nextPrice && curPrice && nextPrice !== curPrice) {
    const diff = nextPrice - curPrice;
    parts.push(`${formatPrice(Math.abs(diff))} ${diff > 0 ? 'higher' : 'lower'}`);
  }

  const nextEarnest =
    nextTerms.earnest_money === '' || nextTerms.earnest_money == null
      ? null
      : Number(nextTerms.earnest_money);
  const curEarnest = Number(currentTerms.earnest_money) || 0;
  if (nextEarnest != null && nextEarnest !== curEarnest) {
    const diff = nextEarnest - curEarnest;
    parts.push(`${formatPrice(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'} earnest`);
  }

  const dayDiff = dateOnlyDayDiff(nextTerms.closing_date, currentTerms.closing_date);
  if (dayDiff) {
    const n = Math.abs(dayDiff);
    parts.push(`closing ${n} day${n === 1 ? '' : 's'} ${dayDiff < 0 ? 'earlier' : 'later'}`);
  }

  return parts;
};

/**
 * Format a timestamp into a readable string.
 */
const formatTime = (timestamp) => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Get user-facing label for a timeline entry type.
 */
const getEntryTypeLabel = (type) => {
  switch (type) {
    case 'offer': return 'INITIAL OFFER';
    case 'counter': return 'COUNTER OFFER';
    // An acceptance is not a counter-offer: a counter-offer rejects the
    // standing offer and replaces it, an acceptance forms the contract.
    case 'acceptance': return 'OFFER ACCEPTED';
    case 'rejection': return 'OFFER REJECTED';
    case 'comment': return 'COMMENT';
    case 'status_change': return 'STATUS UPDATE';
    default: return type?.toUpperCase() || 'UPDATE';
  }
};

/**
 * Get CSS class suffix for a timeline entry type.
 */
const getEntryTypeClass = (type) => {
  switch (type) {
    case 'offer': return 'offer';
    case 'counter': return 'counter';
    case 'acceptance': return 'acceptance';
    case 'rejection': return 'rejection';
    case 'comment': return 'comment';
    case 'status_change': return 'status';
    default: return 'comment';
  }
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

/**
 * StatusBadge — color-coded negotiation status pill.
 */
const StatusBadge = ({ status }) => {
  const normalized = (status || 'pending').toLowerCase();
  return (
    <span className={`negotiation-status-badge ${normalized}`}>
      {status || 'Pending'}
    </span>
  );
};

/**
 * NegotiationHeader — property info, parties, and current price.
 */
const NegotiationHeader = ({ workspace, myRole }) => {
  const property = workspace?.property || {};
  const buyer = workspace?.buyer || {};
  const seller = workspace?.seller || {};
  const terms = workspace?.current_terms || {};
  const youSuffix = (label) => (myRole === label.toLowerCase() ? ' (You)' : '');

  return (
    <div className="negotiation-header">
      <div className="negotiation-header-top">
        <div className="negotiation-property-info">
          <h2>{property.address || `Property #${property.id || '—'}`}</h2>
          <p className="negotiation-property-address">
            <span className="negotiation-property-tag" title="Internal negotiation reference">
              N#{workspace?.id}
            </span>
            <span className="negotiation-property-parties">
              {buyer?.first_name || 'Buyer'} ↔ {seller?.first_name || 'Seller'}
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* A closed offer (legal copy §15.3) says so whatever the workspace's own status is.
              An accepted one says "accepted": the workspace's own "completed" read as if the
              deal had closed, when signing and contingencies are still ahead. */}
          <StatusBadge
            status={['closed', 'accepted'].includes(workspace?.offer_status)
              ? workspace.offer_status
              : (workspace?.status || workspace?.offer_status)}
          />
          <div className="negotiation-current-price">
            <div className="price-label">Current Price</div>
            <div className="price-value">{formatPrice(terms.offer_price)}</div>
          </div>
        </div>
      </div>
      <div className="negotiation-header-parties">
        <div className="negotiation-party">
          <div
            className="party-avatar buyer"
            role="img"
            aria-label={`${buyer.first_name || 'Buyer'} avatar`}
          >
            {(buyer.first_name || 'B').charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="party-info-name">{buyer.first_name && buyer.last_name ? `${buyer.first_name} ${buyer.last_name}` : `Buyer #${buyer.id || '—'}`}</div>
            <div className="party-info-role">Buyer{youSuffix('Buyer')}</div>
          </div>
        </div>
        <div className="negotiation-party">
          <div
            className="party-avatar seller"
            role="img"
            aria-label={`${seller.first_name || 'Seller'} avatar`}
          >
            {(seller.first_name || 'S').charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="party-info-name">{seller.first_name && seller.last_name ? `${seller.first_name} ${seller.last_name}` : `Seller #${seller.id || '—'}`}</div>
            <div className="party-info-role">Seller{youSuffix('Seller')}</div>
          </div>
        </div>
      </div>
      {/* Which paperwork this deal uses (design §8.4). */}
      <WorkspacePaperworkRow workspace={workspace} />
    </div>
  );
};

/**
 * CurrentTermsSummary — a compact, always-visible snapshot of the terms
 * currently on the table (the latest priced offer or counter). It pins to the
 * top of the negotiation column so a party deciding whether to accept, counter,
 * or reject never has to scroll back up the timeline to re-read the live
 * numbers. Renders nothing before the first priced offer exists. Exported for
 * unit testing, like NegotiationTimeline and CounterOfferForm.
 */
export const CurrentTermsSummary = ({ terms }) => {
  const price = terms?.offer_price;
  if (!price) return null;

  const financingSummary = getFinancingTermsSummary(
    terms.contingencies, financingLoanAmount(terms.offer_price, terms.down_payment_percent),
  );
  const titleSummary = getTitlePeriodsSummary(terms.contingencies);
  const contingencyKeys = getSelectedContingencyKeys(terms.contingencies);

  return (
    <div className="current-terms" data-testid="current-terms-summary">
      <div className="current-terms-head">
        <span className="current-terms-label">Terms on the table</span>
        <span className="current-terms-price" data-testid="current-terms-price">
          {formatPrice(price)}
        </span>
      </div>
      <div className="current-terms-tags">
        {terms.down_payment_percent > 0 && (
          <span className="current-terms-tag">{terms.down_payment_percent}% down</span>
        )}
        {terms.earnest_money > 0 && (
          <span className="current-terms-tag">{formatPrice(terms.earnest_money)} earnest</span>
        )}
        {terms.closing_date && (
          <span className="current-terms-tag">Close {formatDateOnly(terms.closing_date)}</span>
        )}
        {contingencyKeys.map((k) => (
          <span key={k} className="current-terms-tag">
            {getContingencyLabelWithDeadline(k, terms.contingencies)}
          </span>
        ))}
        {financingSummary && <span className="current-terms-tag">{financingSummary}</span>}
        {titleSummary && <span className="current-terms-tag">{titleSummary}</span>}
      </div>
    </div>
  );
};

/**
 * TimelineEntry — renders a single timeline event.
 */
const TimelineEntry = ({ entry, buyerId, previousTerms = null }) => {
  const type = entry.type || 'comment';
  const isBuyerEntry = entry.created_by_role === 'buyer';
  const partyClass = isBuyerEntry ? 'buyer-entry' : 'seller-entry';

  const entryClasses = [
    'timeline-entry',
    `timeline-entry-${getEntryTypeClass(type)}`,
    (type === 'counter' || type === 'offer' || type === 'acceptance' || type === 'rejection') ? partyClass : '',
  ].filter(Boolean).join(' ');

  const terms = entry.terms || {};
  const financingSummary = getFinancingTermsSummary(
    terms.contingencies, financingLoanAmount(terms.offer_price, terms.down_payment_percent),
  );
  const titleSummary = getTitlePeriodsSummary(terms.contingencies);

  return (
    <div className={entryClasses}>
      <div className="timeline-entry-header">
        <span className={`timeline-entry-type timeline-type-${getEntryTypeClass(type)}`}>
          {getEntryTypeLabel(type)}
        </span>
        <span className="timeline-entry-time">{formatTime(entry.created_at)}</span>
      </div>

      {entry.created_by_name && (
        <div className="timeline-entry-author">
          {entry.created_by_name}
          {entry.created_by_role && <span className="timeline-author-role"> ({entry.created_by_role})</span>}
        </div>
      )}

      <div className="timeline-entry-body">
        {/* Price display for the terms-carrying entry types. An acceptance
            carries the agreed terms verbatim, so it renders the same summary. */}
        {(type === 'offer' || type === 'counter' || type === 'acceptance') && terms.offer_price && (
          <>
            <div className="timeline-entry-price">{formatPrice(terms.offer_price)}</div>
            <div className="timeline-entry-terms">
              {terms.down_payment_percent && (
                <span className="timeline-term-tag">{terms.down_payment_percent}% down</span>
              )}
              {terms.earnest_money > 0 && (
                <span className="timeline-term-tag" data-testid="timeline-earnest">
                  {formatPrice(terms.earnest_money)} earnest
                </span>
              )}
              {terms.closing_date && (
                <span className="timeline-term-tag">Close {formatDateOnly(terms.closing_date)}</span>
              )}
              {getSelectedContingencyKeys(terms.contingencies).map((k) => (
                <span key={k} className="timeline-term-tag">{getContingencyLabelWithDeadline(k, terms.contingencies)}</span>
              ))}
              {getCureElectionSummary(terms.contingencies) && (
                <span className="timeline-term-tag" data-testid="timeline-cure">
                  {getCureElectionSummary(terms.contingencies)}
                </span>
              )}
              {/* WI.offer.financing_terms.summary / title_periods.summary (QA N13). */}
              {financingSummary && (
                <span className="timeline-term-tag" data-testid="timeline-financing-terms">{financingSummary}</span>
              )}
              {titleSummary && (
                <span className="timeline-term-tag" data-testid="timeline-title-periods">{titleSummary}</span>
              )}
            </div>
            {/* WI.offer.cure.counter_note: a seller counter that changes the 7(d) cure right. */}
            {type === 'counter' && entry.created_by_role === 'seller' && previousTerms
              && cureElectionChanged(previousTerms.contingencies, terms.contingencies) && (
              <p className="timeline-cure-note" role="note" data-testid="timeline-cure-counter-note">
                {CURE_COPY.counterNote}
              </p>
            )}
            {/* Either party's counter that changes the loan terms or the title periods. */}
            {type === 'counter' && previousTerms && financingSummary
              && financingTermsChanged(previousTerms.contingencies, terms.contingencies) && (
              <p className="timeline-cure-note" role="note" data-testid="timeline-financing-counter-note">
                {FINANCING_TERMS_COPY.counterNote}
              </p>
            )}
            {type === 'counter' && previousTerms && titleSummary
              && titlePeriodsChanged(previousTerms.contingencies, terms.contingencies) && (
              <p className="timeline-cure-note" role="note" data-testid="timeline-title-counter-note">
                {TITLE_PERIODS_COPY.counterNote}
              </p>
            )}
          </>
        )}

        {/* The buyer's free-text terms from the offer wizard. */}
        {type === 'offer' && terms.additional_terms && (
          <p className="timeline-entry-note" data-testid="timeline-additional-terms">
            <span className="timeline-entry-note-label">Additional terms: </span>
            {terms.additional_terms}
          </p>
        )}

        {/* QA F15/F16: the note a party wrote with a counter, or the seller's
            reason for a rejection. */}
        {(type === 'counter' || type === 'rejection') && entry.note && (
          <p className="timeline-entry-note" data-testid="timeline-entry-note">
            <span className="timeline-entry-note-label">{type === 'rejection' ? 'Reason: ' : 'Note: '}</span>
            {entry.note}
          </p>
        )}

        {/* Comment text */}
        {type === 'comment' && (
          <p>{entry.comment_text}</p>
        )}
      </div>
    </div>
  );
};

/**
 * The terms of the latest terms-carrying entry (offer or counter) before
 * `index`, or null. Used to tell when a counter changes the cure right.
 */
const previousTermsBefore = (timeline, index) => {
  for (let i = index - 1; i >= 0; i -= 1) {
    const entry = timeline[i];
    if ((entry?.type === 'offer' || entry?.type === 'counter') && entry.terms) return entry.terms;
  }
  return null;
};

// A long back-and-forth buries the recent, actionable rounds under old ones.
// Past these many entries the timeline collapses to the most recent few, behind
// a toggle that reveals the rest.
const TIMELINE_COLLAPSE_AFTER = 5;
const TIMELINE_RECENT_VISIBLE = 3;

/**
 * NegotiationTimeline — chronological feed of all entries (oldest first). Long
 * histories collapse to the most recent entries behind a "show earlier" toggle
 * so the latest rounds stay in view. Exported for unit testing, like
 * `CounterOfferForm` below.
 */
export const NegotiationTimeline = ({ timeline, buyerId }) => {
  const [expanded, setExpanded] = useState(false);

  if (!timeline || timeline.length === 0) {
    return (
      <div className="negotiation-empty" style={{ padding: '40px 24px' }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>No timeline entries yet.</p>
      </div>
    );
  }

  const collapsible = timeline.length > TIMELINE_COLLAPSE_AFTER;
  // Number of oldest entries hidden while collapsed; the true index is still
  // passed to each visible entry so `previousTermsBefore` keeps comparing a
  // counter against its real predecessor even when that predecessor is hidden.
  const hiddenCount = collapsible && !expanded
    ? timeline.length - TIMELINE_RECENT_VISIBLE
    : 0;

  return (
    <div className="negotiation-timeline">
      {collapsible && (
        <button
          type="button"
          className="timeline-collapse-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          data-testid="timeline-collapse-toggle"
        >
          {expanded
            ? 'Show fewer events'
            : `Show ${hiddenCount} earlier event${hiddenCount === 1 ? '' : 's'}`}
        </button>
      )}
      {timeline.map((entry, idx) => {
        if (idx < hiddenCount) return null;
        return (
          <TimelineEntry
            key={entry.id || idx}
            entry={entry}
            buyerId={buyerId}
            previousTerms={previousTermsBefore(timeline, idx)}
          />
        );
      })}
    </div>
  );
};

/**
 * CounterOfferForm — inline form for submitting a counter-offer.
 *
 * Carries the same price sanity gate as OfferManagement's CounterOfferModal
 * (QA sheet T-41). This is the workspace's own counter surface and posts to
 * the same endpoint, so a guard on only the modal leaves the typo path open.
 *
 * Exported for unit testing, following `pickActivePurchaseAgreement` above.
 */
export const CounterOfferForm = ({
  currentTerms,
  currentPrice,
  listPrice,
  property,
  myRole = 'buyer',
  onSubmit,
  onCancel,
  loading,
  // The rejected submit's API error (or its `detail`); a 422
  // offer_contingencies_invalid is shown next to the contingencies it names.
  submitError = null,
}) => {
  // Offer the contingencies this property supports, plus anything the current
  // terms already carry so a counter can never silently drop a negotiated term.
  const contingencyOptions = useMemo(
    () => getContingencyOptionsForOffer({ contingencies: currentTerms?.contingencies }, property),
    [currentTerms, property],
  );
  // Pre-1978 or unknown-year WI home: the buyer's inspect/waive election.
  const leadPaintMode = useMemo(
    () => leadPaintElectionMode(property, { contingencies: currentTerms?.contingencies }),
    [currentTerms, property],
  );
  // The election is the buyer's right: a seller's counter shows it read-only
  // and carries it forward; only the buyer is asked.
  const leadPaintAskedMode = myRole === 'seller' ? null : leadPaintMode;
  // WI offer: the 7(d) cure election, asked while a curable contingency is selected.
  const cureCategory = useMemo(
    () => cureElectionCategory(property, { contingencies: currentTerms?.contingencies }),
    [currentTerms, property],
  );
  // WI offer: the title periods, and the loan terms while financing is selected (QA N13).
  const termsCategory = useMemo(
    () => offerTermsCategory(property, { contingencies: currentTerms?.contingencies }),
    [currentTerms, property],
  );

  const getInitialForm = useCallback(() => {
    const recorded = normalizeContingencies(currentTerms?.contingencies);
    // Negotiated deadlines (`<key>_days`), the lead-paint and cure elections,
    // the loan terms and the title periods carry over too.
    const contingencies = {
      ...normalizeContingencyDeadlines(currentTerms?.contingencies),
      ...leadPaintElectionFromOffer(currentTerms?.contingencies),
      ...cureElectionFromOffer(currentTerms?.contingencies),
      ...financingTermsFromOffer(currentTerms?.contingencies),
      ...titlePeriodsFromOffer(currentTerms?.contingencies),
    };
    contingencyOptions.forEach(({ key }) => { contingencies[key] = !!recorded[key]; });
    const closingDate =
      currentTerms?.closing_date
      || toDateOnly(new Date(Date.now() + 45 * 24 * 60 * 60 * 1000));
    return {
      offer_price: currentTerms?.offer_price ?? currentPrice ?? 0,
      down_payment_percent: currentTerms?.down_payment_percent ?? 20,
      // The deposit on the table; blank keeps it (QA D1/N15).
      earnest_money: initialCounterEarnest(currentTerms?.earnest_money),
      closing_date: closingDate,
      // A counter is a new offer with its own acceptance deadline; it does
      // not inherit the one it answers.
      expires_on: getDefaultExpiresOn(closingDate, property?.timezone),
      contingencies,
      // Untouched contingencies are omitted from the counter, so the server
      // carries the stored terms forward (a price-only counter on an older
      // offer, e.g. one with a blank deadline, still goes through).
      contingenciesTouched: false,
      changes_description: '',
    };
  }, [contingencyOptions, currentPrice, currentTerms, property?.timezone]);

  const [form, setForm] = useState(getInitialForm);
  // Price gate state. `priceTouched` keeps the gate disarmed until the user
  // edits the price — the form opens pre-filled with the other party's number,
  // and an ordinary lowball is already more than 20% off asking.
  const [priceConfirmed, setPriceConfirmed] = useState(false);
  const [priceTouched, setPriceTouched] = useState(false);

  useEffect(() => {
    setForm(getInitialForm());
    setPriceConfirmed(false);
    setPriceTouched(false);
  }, [getInitialForm]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePriceChange = (value) => {
    setPriceTouched(true);
    setPriceConfirmed(false);
    handleChange('offer_price', value);
  };

  // Live "what does this counter change" summary vs. the offer on the table.
  const deltaParts = describeCounterDelta(
    { offer_price: form.offer_price, earnest_money: form.earnest_money, closing_date: form.closing_date },
    currentTerms,
  );

  const deviation = priceDeviation(form.offer_price, listPrice);
  const priceOutOfRange =
    priceTouched && isCounterPriceOutOfRange(form.offer_price, listPrice);
  const needsPriceConfirm = priceOutOfRange && !priceConfirmed;
  const earnestError = counterEarnestError(form.earnest_money, form.offer_price);
  const currentEarnest = initialCounterEarnest(currentTerms?.earnest_money);
  const timeZone = property?.timezone;
  const expiresBounds = getExpirationBounds(form.closing_date, timeZone);
  const expirationError = getExpirationError(form.expires_on, form.closing_date, timeZone);
  const recipient = myRole === 'seller' ? 'buyer' : 'seller';

  const handleContingencyChange = (key, checked) => {
    setForm((prev) => {
      let contingencies = { ...prev.contingencies, [key]: checked };
      if (!checked) delete contingencies[deadlineKey(key)];
      if (!checked && key === FINANCING_KEY) contingencies = withoutFinancingTerms(contingencies);
      return { ...prev, contingencies, contingenciesTouched: true };
    });
  };

  const handleDeadlineChange = (key, text) => {
    setForm((prev) => ({
      ...prev,
      contingencies: { ...prev.contingencies, [deadlineKey(key)]: text },
      contingenciesTouched: true,
    }));
  };

  const handleTermsChange = (contingencies) =>
    setForm((prev) => ({ ...prev, contingencies, contingenciesTouched: true }));

  // Deadlines must be 1-365 days; on every WI offer each selected contingency
  // needs one (legal L4 §9.7; user decision for home and condo); a term the
  // listing's offer can't print must go; the lead-paint and cure elections
  // are required where they are asked, and on a WI offer the title periods
  // and the financing contingency's loan terms (QA N13).
  const deadlineError = offerElectionError(form.contingencies, contingencyOptions, leadPaintAskedMode, cureCategory, {
    category: termsCategory,
    downPaymentPercent: form.down_payment_percent,
  });
  const cureAsked = cureElectionAsked(cureCategory, form.contingencies);
  const financingAsked = financingTermsAsked(termsCategory, form.contingencies);
  // A 100% down payment leaves the financing contingency no loan, even when
  // the contingencies are carried forward untouched (the server refuses it).
  const fullDownPaymentError = financingAsked
    ? financingTermsFieldErrors(form.contingencies, termsCategory, form.down_payment_percent)[FINANCING_KEY] || null
    : null;
  // Only elections the user changed are checked and sent; untouched ones
  // carry forward. A term this listing's offer can't print blocks either way.
  const deadlinesInvalid = form.contingenciesTouched
    ? Boolean(deadlineError)
    : unavailableSelectedKeys(form.contingencies, contingencyOptions).length > 0 || Boolean(fullDownPaymentError);
  const hasLandContingencies = contingencyOptions.some((o) => o.group === 'land');
  const showDeadlineNote =
    !hasLandContingencies && isWisconsinHomeOffer(property, { contingencies: currentTerms?.contingencies });

  // The server's refusal (422 offer_contingencies_invalid) describes the
  // elections that were submitted; it stops applying once they change.
  const contingencyError = useMemo(() => parseContingencyElectionError(submitError), [submitError]);
  const submittedContingencies = useRef(null);
  const contingencyErrorActive =
    Boolean(contingencyError) && form.contingencies === submittedContingencies.current;
  const serverErrors = contingencyErrorActive ? contingencyError.fieldErrors : {};

  const handleSubmit = (e) => {
    e.preventDefault();
    if (needsPriceConfirm || deadlinesInvalid || expirationError || earnestError) return;
    submittedContingencies.current = form.contingencies;
    // A changed set goes out whole, every offered contingency as an explicit
    // election. Unchanged, it is omitted: the server keeps the stored terms.
    // A blank deposit is omitted too, which keeps the one on the table.
    const { contingenciesTouched, contingencies, earnest_money: earnest, ...rest } = form;
    const terms = { ...rest, ...counterEarnestPayload(earnest) };
    onSubmit(
      contingenciesTouched
        ? {
            ...terms,
            contingencies: serializeContingencies(contingencies, {
              explicitKeys: contingencyOptions.map((o) => o.key),
              leadPaintMode,
              cureCategory,
              termsCategory,
            }),
          }
        : terms,
    );
  };

  return (
    <form className="counter-offer-form" onSubmit={handleSubmit} data-testid="counter-offer-form">
      <h3>Submit Counter Offer</h3>
      {/* Live comparison to the offer on the table, so a party can see what
          their counter actually moves without doing the arithmetic. */}
      <div className="counter-delta" data-testid="counter-delta">
        <span className="counter-delta-label">vs. current offer:</span>
        {deltaParts.length === 0 ? (
          <span className="counter-delta-same" data-testid="counter-delta-same">no change yet</span>
        ) : (
          deltaParts.map((part) => (
            <span key={part} className="counter-delta-tag">{part}</span>
          ))
        )}
      </div>
      <div className="counter-form-grid">
        <div className="counter-form-group">
          <label htmlFor="counter-price">Offer Price ($)</label>
          <input
            id="counter-price"
            type="number"
            value={form.offer_price}
            onChange={(e) => handlePriceChange(parseFloat(e.target.value) || 0)}
            min="1"
            step="1"
            required
            aria-describedby={priceOutOfRange ? 'counter-price-warning' : undefined}
            aria-invalid={needsPriceConfirm || undefined}
          />
          {Number(listPrice) > 0 && (
            <small className="counter-form-hint">
              Asking price is ${Number(listPrice).toLocaleString('en-US')}.
            </small>
          )}
          {priceOutOfRange && (
            <div
              id="counter-price-warning"
              className="counter-price-warning"
              data-testid="counter-price-warning"
            >
              <p className="counter-price-warning__text">
                That is <strong>
                  {Math.abs(deviation * 100).toFixed(0)}% {deviation > 0 ? 'above' : 'below'}
                </strong>{' '}
                the ${Number(listPrice).toLocaleString('en-US')} asking price. Double-check
                you have not added or dropped a digit.
              </p>
              <label className="counter-price-warning__check">
                <input
                  type="checkbox"
                  checked={priceConfirmed}
                  onChange={(e) => setPriceConfirmed(e.target.checked)}
                  disabled={loading}
                  data-testid="counter-price-confirm"
                />
                <span>
                  Yes, ${Number(form.offer_price).toLocaleString('en-US')} is the price I mean.
                </span>
              </label>
            </div>
          )}
        </div>
        <div className="counter-form-group">
          <label htmlFor="counter-down">Down Payment (%)</label>
          <input
            id="counter-down"
            type="number"
            value={form.down_payment_percent}
            onChange={(e) => handleChange('down_payment_percent', parseFloat(e.target.value) || 0)}
            min="0"
            max="100"
            step="0.1"
            required
          />
        </div>
        <div className="counter-form-group">
          <label htmlFor="counter-earnest">Earnest Money ($)</label>
          <input
            id="counter-earnest"
            type="number"
            value={form.earnest_money}
            onChange={(e) => handleChange('earnest_money', e.target.value)}
            min="1"
            step="1"
            placeholder={currentEarnest || 'None'}
            aria-describedby="counter-earnest-hint"
            aria-invalid={!!earnestError || undefined}
            data-testid="counter-earnest"
          />
          {earnestError ? (
            <small
              id="counter-earnest-hint"
              className="counter-form-hint counter-form-hint--error"
              role="alert"
              data-testid="counter-earnest-error"
            >
              {earnestError}
            </small>
          ) : (
            <small id="counter-earnest-hint" className="counter-form-hint">
              The purchase agreement prints this deposit.
              {currentEarnest
                ? ` Leave blank to keep $${Number(currentEarnest).toLocaleString('en-US')}.`
                : ' Leave blank for no deposit.'}
            </small>
          )}
        </div>
        <div className="counter-form-group">
          <label htmlFor="counter-close">Closing Date</label>
          <input
            id="counter-close"
            type="date"
            value={form.closing_date}
            onChange={(e) => handleChange('closing_date', e.target.value)}
            required
          />
        </div>
        <div className="counter-form-group">
          <label htmlFor="counter-expires">Counter Valid Until</label>
          <input
            id="counter-expires"
            type="date"
            value={form.expires_on}
            min={expiresBounds.min}
            max={expiresBounds.max}
            onChange={(e) => handleChange('expires_on', e.target.value)}
            aria-describedby="counter-expires-hint"
            aria-invalid={!!expirationError || undefined}
            required
          />
          {expirationError ? (
            <small id="counter-expires-hint" className="counter-form-hint counter-form-hint--error" role="alert">
              {expirationError}
            </small>
          ) : (
            <small id="counter-expires-hint" className="counter-form-hint">
              The {recipient} must accept your counter in cutthe6.com by{' '}
              {deadlineTimeLabel(form.expires_on, timeZone)} on this date, the
              property&apos;s local time, or it expires.
              {myRole === 'buyer' && " You can withdraw it any time before it's accepted."}
              {' '}Once you counter, the earlier offer can no longer be accepted.
            </small>
          )}
          <OfferDeadlineDisclaimer />
        </div>
        <div className="counter-form-group">
          <label>Contingencies</label>
          {hasLandContingencies && <LandContingencyNote viewerRole={myRole} />}
          {showDeadlineNote && <ContingencyDeadlineNote viewerRole={myRole} />}
          {contingencyErrorActive && (
            <ContingencyErrorBanner
              error={contingencyError}
              options={contingencyOptions}
              extraKeys={[
                ...(leadPaintAskedMode ? ['lead_paint'] : []),
                ...(cureAsked ? [CURE_ELECTION_FIELD] : []),
                ...(financingAsked ? [FINANCING_TERMS_FIELD] : []),
                ...(termsCategory ? [TITLE_PERIODS_FIELD] : []),
              ]}
            />
          )}
          {!form.contingenciesTouched && deadlineError && !deadlinesInvalid && (
            <small className="counter-form-hint" data-testid="counter-contingencies-unchanged">
              {CONTINGENCIES_UNCHANGED_NOTE}
            </small>
          )}
          <div className="counter-form-contingencies">
            {contingencyOptions.map(({ key, label, deadline, deadlineRequired, unavailable }) => (
              <div key={key} className="counter-form-contingency">
                <label>
                  <input
                    type="checkbox"
                    checked={!!form.contingencies[key]}
                    onChange={(e) => handleContingencyChange(key, e.target.checked)}
                  />
                  {label}
                </label>
                {unavailable && form.contingencies[key] && (
                  <ContingencyFieldError field={`${key}-unavailable`} message={CONTINGENCY_UNAVAILABLE_MESSAGE} />
                )}
                <ContingencyFieldError field={key} message={serverErrors[key]} />
                {deadline && form.contingencies[key] && (
                  <ContingencyDeadlineField
                    contingencyKey={key}
                    label={label}
                    value={form.contingencies[deadlineKey(key)]}
                    required={deadlineRequired}
                    serverError={serverErrors[deadlineKey(key)]}
                    onChange={(text) => handleDeadlineChange(key, text)}
                    disabled={loading}
                  />
                )}
              </div>
            ))}
          </div>
          <FinancingTermsField
            category={termsCategory}
            contingencies={form.contingencies}
            onChange={handleTermsChange}
            amount={financingLoanAmount(form.offer_price, form.down_payment_percent)}
            downPaymentPercent={form.down_payment_percent}
            viewerRole={myRole}
            disabled={loading}
            serverErrors={serverErrors}
          />
          <TitlePeriodsField
            category={termsCategory}
            contingencies={form.contingencies}
            onChange={handleTermsChange}
            closingDate={form.closing_date}
            timeZone={timeZone}
            viewerRole={myRole}
            disabled={loading}
            serverErrors={serverErrors}
          />
          <LeadPaintElectionField
            mode={leadPaintAskedMode}
            contingencies={form.contingencies}
            onChange={(contingencies) => setForm((prev) => ({ ...prev, contingencies, contingenciesTouched: true }))}
            disabled={loading}
            serverErrors={serverErrors}
          />
          {myRole === 'seller' && (
            <LeadPaintElectionCarried mode={leadPaintMode} contingencies={form.contingencies} />
          )}
          <CureElectionField
            category={cureCategory}
            contingencies={form.contingencies}
            onChange={(contingencies) => setForm((prev) => ({ ...prev, contingencies, contingenciesTouched: true }))}
            disabled={loading}
            serverErrors={serverErrors}
          />
        </div>
        <div className="counter-form-group full-width">
          <label htmlFor="counter-desc">Notes</label>
          <textarea
            id="counter-desc"
            value={form.changes_description}
            onChange={(e) => handleChange('changes_description', e.target.value)}
            placeholder="Describe your counter-offer rationale..."
          />
        </div>
      </div>
      <div className="counter-form-actions">
        <button type="button" className="action-btn action-btn-comment" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button
          type="submit"
          className="action-btn action-btn-counter"
          disabled={loading || needsPriceConfirm || deadlinesInvalid || !!expirationError || !!earnestError}
          title={
            needsPriceConfirm
              ? 'Confirm the price is intentional before submitting.'
              : earnestError
                ? earnestError
                : deadlinesInvalid
                ? (form.contingenciesTouched ? deadlineError : fullDownPaymentError || deadlineError)
                : expirationError || undefined
          }
        >
          {loading ? 'Submitting...' : 'Submit Counter'}
        </button>
      </div>
    </form>
  );
};

/**
 * CommentInput — text input for adding comments.
 */
export const CommentInput = ({ onSubmit, onCancel, loading }) => {
  const [text, setText] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitError(null);
    try {
      // Only clear the box once the comment is actually sent — otherwise a
      // failed post would throw away what the user typed.
      await onSubmit(trimmed);
      setText('');
    } catch (_err) {
      setSubmitError("Your comment couldn't be sent. Please try again.");
    }
  };

  return (
    <form className="comment-input" onSubmit={handleSubmit}>
      <div className="comment-input-field">
        <label htmlFor="negotiation-comment" className="comment-label">Add a comment</label>
        <textarea
          id="negotiation-comment"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment to the negotiation..."
          aria-label="Negotiation comment"
          aria-invalid={submitError ? true : undefined}
        />
        {submitError && (
          <p className="comment-error" role="alert" data-testid="comment-error">{submitError}</p>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button type="submit" className="action-btn action-btn-counter" disabled={loading || !text.trim()}>
          {loading ? 'Sending...' : 'Send'}
        </button>
        <button type="button" className="action-btn action-btn-comment" onClick={onCancel} style={{ fontSize: 12, padding: '6px 12px' }}>
          Cancel
        </button>
      </div>
    </form>
  );
};

/**
 * Shown to both parties when cutthe6.com can't prepare this state's purchase
 * agreement, so nobody accepts expecting one to arrive.
 */
const docsUnsupportedText = (state, accepted = false) =>
  `cutthe6.com can't prepare the purchase agreement in ${state || 'this state'} yet. `
  // Legal review 2026-10-24: "agree on terms here … still need" keeps the
  // platform acceptance apart from the signed contract.
  + (accepted ? 'You will need' : "If you agree on terms here, you'll still need")
  + ' to prepare and sign a purchase agreement outside cutthe6.com, '
  + 'for example with a real estate attorney.';

/**
 * AcceptedCelebration — banner shown when a deal is accepted, with next steps.
 */
const AcceptedCelebration = ({
  price,
  propertyAddress,
  propertyState,
  documentsSupported,
  provisioningEnvelope,
  agreementInFlight,
  sendBlockedNote,
  onGenerateDocuments,
  onGoToDocuments,
}) => (
  <div className="accepted-celebration">
    <div className="celebration-confetti" aria-hidden="true">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="confetti-piece" />
      ))}
    </div>
    <div className="celebration-icon">🎉</div>
    <h2>Deal Accepted!</h2>
    <p>
      Congratulations — both parties have reached an agreement
      {propertyAddress ? <> on <strong>{propertyAddress}</strong></> : null}.
    </p>
    {price && <div className="celebration-price">{formatPrice(price)}</div>}
    <div className="celebration-actions">
      <button
        className="celebration-btn celebration-btn-primary"
        onClick={onGenerateDocuments}
        disabled={!documentsSupported || provisioningEnvelope}
        title={
          documentsSupported
            ? undefined
            : `Document generation in ${propertyState || 'this state'} is not yet available`
        }
      >
        {provisioningEnvelope
          ? <>⏳ Preparing purchase agreement envelope…</>
          : documentsSupported && agreementInFlight
          ? <>✍️ Review &amp; Sign Purchase Agreement</>
          : documentsSupported
          ? <>📝 Generate Purchase Agreement{propertyAddress ? ` for ${propertyAddress}` : ''}</>
          : <>🕒 Document generation coming to {propertyState || 'this state'}</>}
      </button>
      {typeof onGoToDocuments === 'function' && (
        <button className="celebration-btn celebration-btn-secondary" onClick={onGoToDocuments}>
          📋 View All Documents
        </button>
      )}
    </div>
    {sendBlockedNote && !provisioningEnvelope && (
      <p className="celebration-helper-text celebration-attestation-note" data-testid="celebration-attestation-note">
        {sendBlockedNote}
      </p>
    )}
    {provisioningEnvelope && (
      <p className="celebration-helper-text">
        We&apos;re generating the agreement and opening the signing workspace automatically.
      </p>
    )}
  </div>
);

/**
 * NegotiationFlowDiagram — two-lane (buyer/seller) state-machine diagram.
 *
 * Shows every step of the negotiation as a side-by-side flow:
 *   Buyer lane (left, blue) ←→ shared state node ←→ Seller lane (right, coral)
 *
 * The current position is highlighted and a "Your Turn" badge surfaces the
 * active party. Terminal paths (Rejected / Withdrawn / Expired) branch off
 * the bottom.
 *
 * Props:
 *   workspace   – workspace object (offer_status, buyer, seller)
 *   timeline    – array of timeline entries to determine last actor
 */
const FLOW_ROUNDS = [
  {
    round: 1,
    buyerAction: { label: 'Submit Offer', detail: 'Price, terms & contingencies', icon: '📝' },
    sharedState: { key: 'pending', label: 'Pending', color: '#4f46e5' },
    sellerAction: { label: 'Review Offer', detail: 'Accept, counter, or reject', icon: '👀' },
    activeWhen: 'pending',
    activeTurn: 'seller',
  },
  {
    round: 2,
    buyerAction: { label: 'Review Counter', detail: 'Accept, counter again, or withdraw', icon: '🔍' },
    sharedState: { key: 'countered', label: 'Countered', color: '#d97706' },
    sellerAction: { label: 'Send Counter', detail: 'Revised price, date, or terms', icon: '💬' },
    activeWhen: 'countered',
    activeTurn: null, // determined dynamically from last timeline actor
  },
  {
    round: 3,
    buyerAction: { label: 'Accept', detail: 'Lock in the deal', icon: '✅' },
    sharedState: { key: 'accepted', label: 'Accepted! 🎉', color: '#38a169' },
    sellerAction: { label: 'Accept', detail: 'Confirm & move to contingencies', icon: '✅' },
    activeWhen: 'accepted',
    activeTurn: 'both',
  },
];

/** Derive who last acted from the timeline. */
const getLastActor = (timeline) => {
  if (!timeline?.length) return null;
  const last = [...timeline]
    .reverse()
    .find((e) => e.type === 'offer' || e.type === 'counter');
  return last?.created_by_role || null;
};

const NegotiationFlowDiagram = ({ workspace, timeline, isWisconsin, recrCompleted, myRole }) => {
  const disclosureTitle = workspaceDisclosureTitle(workspace);
  const offerStatus = (workspace?.offer_status || workspace?.status || '').toLowerCase();
  const lastActor = getLastActor(timeline);

  const activeTurn =
    offerStatus === 'pending' ? 'seller'
    : offerStatus === 'countered' ? (lastActor === 'seller' ? 'buyer' : 'seller')
    : offerStatus === 'accepted' ? 'both'
    : null;

  const isTerminal = ['rejected', 'withdrawn', 'expired', 'closed'].includes(offerStatus);
  // The badge names whose move it is from the viewer's side: "Your Turn" on
  // their own lane, "Buyer's Turn" / "Seller's Turn" on the other.
  const turnLabel = (side) => (side === myRole
    ? 'Your Turn'
    : `${side.charAt(0).toUpperCase() + side.slice(1)}'s Turn`);
  // A revision is a change to the original offer, i.e. a counter — the opening
  // offer itself isn't one, so a brand-new offer reads "0 revisions" (no pill).
  const revisionCount = timeline?.filter((e) => e.type === 'counter').length || 0;
  const expiresHint = workspace?.current_terms?.expires_at
    ? `Expires ${formatDeadline(workspace.current_terms.expires_at, workspace?.property?.timezone)}`
    : null;

  return (
    <div className="nfd-root">
      {/* Title */}
      <div className="nfd-title-bar">
        <div className="nfd-title">
          <span className="nfd-title-icon">🗺️</span>
          <span>Negotiation Roadmap</span>
        </div>
        <div className="nfd-title-meta">
          {revisionCount > 0 && (
            <span className="nfd-pill">
              🔄 {revisionCount} revision{revisionCount !== 1 ? 's' : ''}
            </span>
          )}
          {expiresHint && !isTerminal && offerStatus !== 'accepted' && (
            <span className="nfd-pill nfd-pill--warn">⏰ {expiresHint}</span>
          )}
        </div>
      </div>

      {/* Lane labels */}
      <div className="nfd-lane-labels">
        <span className="nfd-lane-label nfd-lane-label--buyer">Buyer</span>
        <span className="nfd-lane-label nfd-lane-label--seller">Seller</span>
      </div>

      {/* Flow steps */}
      <div className="nfd-flow">
        {FLOW_ROUNDS.map((row, idx) => {
          const isActive = offerStatus === row.activeWhen;
          const buyerTurn = isActive && (activeTurn === 'buyer' || activeTurn === 'both');
          const sellerTurn = isActive && (activeTurn === 'seller' || activeTurn === 'both');
          // Once accepted nobody is waiting on a reply, so no turn badge.
          const showTurnBadge = activeTurn !== 'both';
          const isPast =
            (offerStatus === 'countered' && row.round < 2) ||
            (offerStatus === 'accepted' && row.round < 3) ||
            (isTerminal && row.round < 3);
          const isFuture = !isActive && !isPast;

          return (
            <React.Fragment key={row.round}>
              <div className="nfd-step-group">
              {/* Connector line between steps */}
              {idx > 0 && (
                <div className={`nfd-connector${isPast || isActive ? ' nfd-connector--done' : ''}`}>
                  <div className="nfd-connector-line" />
                </div>
              )}

              <div className={`nfd-step${isActive ? ' nfd-step--active' : ''}${isPast ? ' nfd-step--past' : ''}${isFuture ? ' nfd-step--future' : ''}`}>
                {/* State badge (center) */}
                <div
                  className={`nfd-state${isActive ? ' nfd-state--active' : ''}${isPast ? ' nfd-state--past' : ''}`}
                  style={isActive ? { '--state-color': row.sharedState.color } : {}}
                >
                  <span className="nfd-state-dot" />
                  <span className="nfd-state-text">{row.sharedState.label}</span>
                </div>

                {/* Two-lane cards */}
                <div className="nfd-lanes">
                  {/* Buyer card */}
                  <div className={`nfd-card nfd-card--buyer${buyerTurn ? ' nfd-card--turn' : ''}${isPast ? ' nfd-card--done' : ''}`}>
                    <div className="nfd-card-icon">{isPast ? '✓' : row.buyerAction.icon}</div>
                    <div className="nfd-card-body">
                      <div className="nfd-card-label">{row.buyerAction.label}</div>
                      <div className="nfd-card-detail">{row.buyerAction.detail}</div>
                    </div>
                    {buyerTurn && showTurnBadge && <div className="nfd-badge nfd-badge--buyer">{turnLabel('buyer')}</div>}
                  </div>

                  {/* Seller card */}
                  <div className={`nfd-card nfd-card--seller${sellerTurn ? ' nfd-card--turn' : ''}${isPast ? ' nfd-card--done' : ''}`}>
                    <div className="nfd-card-icon">{isPast ? '✓' : row.sellerAction.icon}</div>
                    <div className="nfd-card-body">
                      <div className="nfd-card-label">{row.sellerAction.label}</div>
                      <div className="nfd-card-detail">{row.sellerAction.detail}</div>
                    </div>
                    {sellerTurn && showTurnBadge && <div className="nfd-badge nfd-badge--seller">{turnLabel('seller')}</div>}
                  </div>
                </div>
              </div>
            </div>

              {/* WI seller disclosure: a cutthe6.com step (the law allows it up to
                  10 days after acceptance), injected between round 2 and round 3 */}
              {isWisconsin && idx === 1 && !['rejected', 'withdrawn', 'expired', 'closed'].includes(offerStatus) && (
                <div className="nfd-step-group">
                  <div className={`nfd-connector${recrCompleted || offerStatus === 'accepted' ? ' nfd-connector--done' : ''}`}>
                    <div className="nfd-connector-line" />
                  </div>
                  <div
                    className={`nfd-recr-gate${recrCompleted ? ' nfd-recr-gate--done' : ' nfd-recr-gate--pending'}`}
                    data-testid="nfd-disclosure-step"
                  >
                    <span className="nfd-recr-gate-icon">
                      {recrCompleted ? '✅' : '📋'}
                    </span>
                    <div className="nfd-recr-gate-body">
                      <div className="nfd-recr-gate-title">{DISCLOSURE_COPY.gateTitle}</div>
                      <div className="nfd-recr-gate-desc">
                        {recrCompleted
                          ? DISCLOSURE_COPY.gateCompleted({ title: disclosureTitle })
                          : DISCLOSURE_COPY.gatePending}
                      </div>
                    </div>
                    {recrCompleted && <span className="nfd-recr-gate-badge">{DISCLOSURE_COPY.gateBadgeCompleted}</span>}
                    {!recrCompleted && recrCompleted !== null && (
                      <span className="nfd-recr-gate-badge nfd-recr-gate-badge--pending">
                        {DISCLOSURE_COPY.gateBadgePending}
                      </span>
                    )}
                  </div>
                  <div className={`nfd-connector${recrCompleted || offerStatus === 'accepted' ? ' nfd-connector--done' : ''}`}>
                    <div className="nfd-connector-line" />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Terminal branch */}
        {isTerminal && (
          <div className="nfd-step-group">
            <div className="nfd-connector nfd-connector--terminal">
              <div className="nfd-connector-line" />
            </div>
            <div className="nfd-terminal-card">
              {offerStatus === 'rejected' && (
                <><span className="nfd-terminal-badge rejected">❌</span><span>Offer <strong>Rejected</strong> by Seller</span></>
              )}
              {offerStatus === 'withdrawn' && (
                <><span className="nfd-terminal-badge withdrawn">↩️</span><span>Offer <strong>Withdrawn</strong> by Buyer</span></>
              )}
              {offerStatus === 'expired' && (
                <><span className="nfd-terminal-badge expired">⏰</span><span>Offer <strong>Expired</strong> — No Response</span></>
              )}
              {offerStatus === 'closed' && (
                <><span className="nfd-terminal-badge closed">📁</span><span>Offer <strong>Closed</strong> by cutthe6.com</span></>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer — next move */}
      {activeTurn && !isTerminal && (
        <div className="nfd-footer">
          Next move:
          <strong>
            {activeTurn === 'both'
              ? ' Both parties proceed to signing'
              : activeTurn === myRole
                ? ' Your turn to respond'
                : ` Waiting for the ${activeTurn} to respond`}
          </strong>
        </div>
      )}
    </div>
  );
};

/**
 * DealProgressTracker — horizontal stepper showing the buying journey.
 */
const DEAL_STEPS = [
  { key: 'offer', label: 'Offer Submitted', icon: '📝' },
  { key: 'negotiation', label: 'Negotiation', icon: '🤝' },
  { key: 'accepted', label: 'Deal Accepted', icon: '✅' },
  { key: 'documents', label: 'Documents', icon: '📄' },
  { key: 'signed', label: 'Signed', icon: '✍️' },
  { key: 'closed', label: 'Closed', icon: '🏠' },
];

const getProgressStep = (offerStatus, docStatus) => {
  const status = (offerStatus || '').toLowerCase();
  if (status === 'rejected' || status === 'withdrawn' || status === 'expired' || status === 'closed') return -1;
  if (docStatus === 'completed') return 5;
  if (docStatus === 'signed' || docStatus === 'viewed') return 4;
  if (docStatus === 'sent' || docStatus === 'generated') return 3;
  if (status === 'accepted') return 2;
  if (status === 'countered') return 1;
  return 0;
};

const DealProgressTracker = ({ offerStatus, docStatus }) => {
  const currentStep = getProgressStep(offerStatus, docStatus);

  if (currentStep === -1) return null;

  const progressPercent = Math.max(0, (currentStep / (DEAL_STEPS.length - 1)) * 100);

  return (
    <div className="deal-progress-tracker">
      <div className="deal-progress-bar">
        <div
          className="deal-progress-fill"
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Deal progress"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="deal-progress-steps">
        {DEAL_STEPS.map((step, idx) => (
          <div
            key={step.key}
            className={`deal-step ${idx <= currentStep ? 'completed' : ''} ${idx === currentStep ? 'active' : ''}`}
          >
            <div className="deal-step-icon">{idx <= currentStep ? '✓' : step.icon}</div>
            <span className="deal-step-label">{step.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};


// ============================================================
// WITHDRAW CONFIRM MODAL
// ============================================================

const WITHDRAW_REASONS = [
  { value: '', label: 'Select a reason…' },
  { value: 'found_another', label: 'Found another property' },
  { value: 'changed_mind', label: 'Changed my mind' },
  { value: 'financing', label: 'Financing fell through' },
  { value: 'price', label: 'Price concerns' },
  { value: 'other', label: 'Other' },
];

// Elements that take focus, minus disabled/hidden ones and the dialog's own
// tabIndex=-1 container. Deliberately not filtered by layout/visibility so it
// behaves the same in jsdom tests and real browsers.
const DIALOG_FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Dialog accessibility for the confirm modals: move focus into the dialog when
 * it opens, keep Tab/Shift+Tab cycling within it (a focus trap, as aria-modal
 * promises), close it on Escape, and restore focus to whatever was focused
 * before (the trigger) when it closes. Returns a ref for the focusable dialog
 * container (give it tabIndex={-1}).
 */
function useDialogA11y(open, onClose) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const node = ref.current;
    if (node) node.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (closeRef.current) closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const items = Array.from(node.querySelectorAll(DIALOG_FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = node.contains(active) && active !== node;
      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open]);
  return ref;
}

/**
 * WithdrawConfirmModal — stepped modal for withdrawing an offer.
 * Requires a reason selection + explicit confirmation checkbox before
 * the destructive action is enabled.
 */
export const WithdrawConfirmModal = ({ open, onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const dialogRef = useDialogA11y(open, onCancel);

  // Reset form whenever modal opens
  useEffect(() => {
    if (open) {
      setReason('');
      setNote('');
      setConfirmed(false);
    }
  }, [open]);

  if (!open) return null;

  const canSubmit = reason !== '' && confirmed && !loading;
  const reasonLabel = WITHDRAW_REASONS.find((r) => r.value === reason)?.label || '';

  return (
    <div className="withdraw-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="withdraw-modal-title">
      <div className="withdraw-modal" ref={dialogRef} tabIndex={-1}>
        {/* Header */}
        <div className="withdraw-modal-header">
          <span className="withdraw-modal-icon">↩</span>
          <h2 id="withdraw-modal-title">Withdraw Your Offer</h2>
          <button className="withdraw-modal-close" onClick={onCancel} aria-label="Close">✕</button>
        </div>

        {/* Warning banner */}
        <div className="withdraw-modal-warning">
          <span className="withdraw-modal-warning-icon">⚠</span>
          <span>This action is <strong>permanent and cannot be undone</strong>. The seller will be notified immediately.</span>
        </div>

        {/* Body */}
        <div className="withdraw-modal-body">
          {/* Reason */}
          <div className="withdraw-modal-field">
            <label htmlFor="withdraw-reason" className="withdraw-modal-label">
              Reason for withdrawal <span className="withdraw-modal-required">*</span>
            </label>
            <select
              id="withdraw-reason"
              className="withdraw-modal-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {WITHDRAW_REASONS.map((r) => (
                <option key={r.value} value={r.value} disabled={r.value === ''}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Optional note */}
          <div className="withdraw-modal-field">
            <label htmlFor="withdraw-note" className="withdraw-modal-label">
              Message to seller <span className="withdraw-modal-optional">(optional)</span>
            </label>
            <textarea
              id="withdraw-note"
              className="withdraw-modal-textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder="e.g. Thank you for your time. I found a property that better fits my needs."
            />
            <span className="withdraw-modal-char-count">{note.length}/300</span>
          </div>

          {/* Summary */}
          {reason && (
            <div className="withdraw-modal-summary">
              <p className="withdraw-modal-summary-label">Summary</p>
              <p className="withdraw-modal-summary-reason">Reason: {reasonLabel}</p>
              {note && <p className="withdraw-modal-summary-note">"{note}"</p>}
            </div>
          )}

          {/* Confirmation checkbox */}
          <label className="withdraw-modal-confirm-row">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="withdraw-modal-checkbox"
            />
            <span>I understand this offer will be permanently withdrawn and the seller will be notified.</span>
          </label>
        </div>

        {/* Footer */}
        <div className="withdraw-modal-footer">
          <button
            className="withdraw-modal-btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Keep Negotiating
          </button>
          <button
            className="withdraw-modal-btn-confirm"
            onClick={() => onConfirm(reason, note)}
            disabled={!canSubmit}
          >
            {loading ? 'Withdrawing…' : 'Withdraw Offer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// WORKSPACE DETAIL VIEW
// ============================================================

/**
 * Where a WI seller completes the disclosure report (RECR or VLDR). The
 * workspace used to generate one from an old database template; the backend
 * now refuses that for WI, because the report is completed and signed on the
 * listing.
 */
const RecrListingGuide = () => (
  <p className="recr-banner-guide" data-testid="recr-listing-guide">
    {DISCLOSURE_COPY.listingGuideLead}{' '}
    <strong>{DISCLOSURE_COPY.listingGuidePath}</strong>
  </p>
);

const WorkspaceDetail = ({ workspaceId, onBack, buyerId, userRole, onNavigateToDocuments }) => {
  // Identity resolution: prefer the explicit `buyerId` prop (parents that
  // already know who the user is pass it in), otherwise fall back to the
  // authenticated user from `useAuth()`. No silent fallback to user 1 — when
  // both are missing we render an auth-required empty state below.
  const { user: currentUser, loading: authLoading } = useAuth();
  const currentUserId = currentUser?.id ?? null;
  const userId = buyerId ?? currentUserId ?? null;
  // Tracks the latest `signature_state_hash` from negotiation status polling.
  // We forward this to any inline DocumentFlowPanel so it refetches when a
  // signature row transitions (e.g., via DocuSign webhook) — events that
  // don't otherwise change offer_status / revision_count / comment_count.
  const [signatureStateHash, setSignatureStateHash] = useState(null);
  const handleSignatureStateChange = useCallback((hash) => {
    setSignatureStateHash(hash);
  }, []);
  const {
    workspace,
    timeline,
    loading,
    error,
    stale,
    refresh,
    fetchTimeline,
    addComment,
    submitCounter,
    acceptOffer,
    rejectOffer,
    withdrawOffer,
  } = useNegotiation(workspaceId, userId, {
    onSignatureStateChange: handleSignatureStateChange,
  });

  const [showCounterForm, setShowCounterForm] = useState(false);
  // The last counter submit's API error, for the counter form to explain.
  const [counterError, setCounterError] = useState(null);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [showDocPanel, setShowDocPanel] = useState(false);
  const [showPaPanel, setShowPaPanel] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const rejectDialogRef = useDialogA11y(rejectModalOpen, () => setRejectModalOpen(false));
  const [docStatus, setDocStatus] = useState(null);
  const [activeDocumentId, setActiveDocumentId] = useState(null);
  const [paReady, setPaReady] = useState(null); // null=loading, true=PA completed, false=not yet
  // A sent (or partly signed) PA already exists for this offer. The CTA opens
  // that same envelope either way (openEnvelopeWorkspace reuses it), but it
  // read "Generate", so a buyer arriving after the seller signed was told to
  // create an agreement that already existed.
  const [paInFlight, setPaInFlight] = useState(false);
  const [paStatusCheckError, setPaStatusCheckError] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [docProvisioning, setDocProvisioning] = useState(false);
  const [docProvisioningError, setDocProvisioningError] = useState(null);
  // Wisconsin sends pause here so the sender can pick each spouse's signing
  // mode (FR-13: an emailed spouse is verified by DocuSign Phone
  // Authentication) and attest signer completeness (FR-12).
  const [sendDialogDocId, setSendDialogDocId] = useState(null);
  // WI marital-status confirmations: { required, complete } reported by
  // MaritalAttestationCard; null until known (or outside WI).
  const [maritalAttestation, setMaritalAttestation] = useState(null);
  const [acceptError, setAcceptError] = useState(null);
  const [recrCompleted, setRecrCompleted] = useState(null); // null = loading, true/false = known

  const terms = workspace?.current_terms || {};
  // The workspace carries the paperwork category at the top level; the land
  // contingencies key off it. Memoized: the counter form resets itself when
  // its property changes, so a fresh object on every render (each status
  // poll re-renders) wiped whatever the user had typed.
  const counterProperty = useMemo(
    () => (workspace?.property
      ? { ...workspace.property, paperwork_category: workspace.paperwork_category }
      : workspace?.property),
    [workspace?.property, workspace?.paperwork_category],
  );
  const offerId = workspace?.offer_id;
  const offerStatus = (workspace?.offer_status || workspace?.status || '').toLowerCase();
  const isAccepted = offerStatus === 'accepted';
  const isTerminal = ['accepted', 'rejected', 'withdrawn', 'expired', 'closed'].includes(offerStatus);
  // Closed by cutthe6.com when the seller deleted the listing (legal copy §15.3): read-only.
  const isClosed = offerStatus === 'closed';
  const closedText = isClosed
    ? offerClosedStatusText({ status: 'closed', close_reason: workspace?.close_reason, listing_deleted: workspace?.listing_deleted })
    : null;
  const propertyState = workspace?.property?.state;
  const isWisconsin = propertyState === 'WI';
  const disclosureTitle = workspaceDisclosureTitle(workspace);
  const disclosureBasis = workspaceDisclosureBasis(workspace);
  const {
    isSupported: isStateSupported,
    loading: statesLoading,
    error: statesError,
  } = useSupportedStates();
  const documentsSupported = isStateSupported(propertyState);
  // A definite "no": the list loaded and this state isn't on it. While it is
  // loading (or failed) isSupported() is false too, and treating that as
  // unsupported would skip the WI agreement envelope after an acceptance.
  const documentsUnsupported = Boolean(propertyState)
    && !statesLoading && !statesError && !documentsSupported;

  // Refresh document readiness (PA and RECR status)
  const refreshDocumentStatus = useCallback(async () => {
    if (!offerId) return;
    setPaStatusCheckError(false);
    if (isWisconsin) {
      // The WI RECR is completed and signed on the LISTING and stored as a
      // house-bound document (offer_id = null), so it never appears in the
      // offer-scoped list below. `workspace.seller_disclosure_completed` is
      // the only authority: an offer-level `seller_disclosure` (the retired
      // template path) doesn't count, and the backend no longer credits one.
      // It doesn't depend on the list, so a failed list can't strand it.
      setRecrCompleted(workspace?.seller_disclosure_completed === true);
    }
    try {
      const resp = await DocumentAPI.listDocuments(offerId, null);
      const list = Array.isArray(resp) ? resp : (resp?.documents || []);
      const paDone = list.some(
        (d) => d.document_type === 'purchase_agreement' && d.status === 'completed'
      );
      setPaReady(paDone);
      setPaInFlight(!paDone && pickActivePurchaseAgreement(list).existingIsSendable);
    } catch (err) {
      console.error('Failed to refresh document status:', err);
      // Don't strand the UI in an indefinite loading/disabled state.
      // If the status check fails, still let users open the PA flow.
      setPaStatusCheckError(true);
      setPaReady(false);
    }
  }, [offerId, isWisconsin, workspace?.seller_disclosure_completed]);

  // Check document readiness: purchase agreement (all offers) + seller disclosure (WI only).
  // These are informational and do not block acceptance.
  useEffect(() => {
    if (!offerId) {
      setRecrCompleted(true);
      setPaReady(false);
      setPaStatusCheckError(false);
      return;
    }
    if (!isWisconsin) {
      setRecrCompleted(true); // no RECR gate outside WI
    } else {
      setRecrCompleted(null); // loading
    }
    setPaReady(null); // loading
    setPaStatusCheckError(false);
    refreshDocumentStatus();
  }, [isWisconsin, offerId, refreshDocumentStatus]);

  // Derive the current user's role in THIS negotiation. Prefer matching against
  // the workspace participants (most authoritative); fall back to the userRole prop.
  const buyerParticipantId = workspace?.buyer?.id;
  const sellerParticipantId = workspace?.seller?.id;
  // Match against the resolved `userId` (prop-first, auth-second). When no user
  // can be resolved this branch falls through to the `userRole` prop instead
  // of guessing — and the auth-required empty state below short-circuits
  // before any of this matters.
  const myRole =
    userId && buyerParticipantId && userId === buyerParticipantId ? 'buyer'
    : userId && sellerParticipantId && userId === sellerParticipantId ? 'seller'
    : (userRole || 'buyer');
  const isBuyer = myRole === 'buyer';
  const isSeller = myRole === 'seller';

  // Determine whose turn it is to respond.
  const lastActor = getLastActor(timeline);
  const activeTurn =
    offerStatus === 'pending' ? 'seller'
    : offerStatus === 'countered' ? (lastActor === 'seller' ? 'buyer' : 'seller')
    : null;
  const isMyTurn = activeTurn === myRole;
  // QA F8: another offer on this listing already holds a live accepted deal.
  const propertyUnderContract = Boolean(workspace?.property_under_contract);
  const canAccept = isMyTurn && !propertyUnderContract;
  const canCounter = isMyTurn;
  // Reject is seller-only (and only against the buyer's last move, i.e. when it's the seller's turn).
  const canReject = isSeller && isMyTurn;
  // Withdraw is buyer-only (buyer can withdraw their own pending offer or last counter).
  const canWithdraw = isBuyer && (offerStatus === 'pending' || offerStatus === 'countered');

  // Why the primary actions (Counter / Accept) are greyed out, spelled out
  // inline for touch: the buttons' title tooltips never show on a phone, and the
  // turn banner has scrolled off by the time you reach the action bar. When it
  // isn't your turn, that's the one reason they're all disabled. Under-contract
  // has its own note above, so it isn't repeated here. Shown only on mobile
  // (CSS) — desktop keeps the banner + tooltips.
  const actionsDisabledReason =
    !propertyUnderContract && activeTurn && !isMyTurn
      ? `Waiting on the ${activeTurn} — Counter and Accept unlock when it's your turn`
      : null;

  const openEnvelopeWorkspace = useCallback(async () => {
    if (!offerId) return null;

    setDocProvisioning(true);
    setDocProvisioningError(null);

    try {
      const listResponse = await DocumentAPI.listDocuments(offerId, null);
      const documents = Array.isArray(listResponse)
        ? listResponse
        : (listResponse?.documents || []);

      // F3: Pure helper extracted for testability. Selects newest envelope-
      // bearing PA over a newer draft, and only treats a row as sendable when
      // it has both an active signing status AND a real envelope_id.
      const { purchaseAgreement, existingIsSendable } = pickActivePurchaseAgreement(documents);

      // Reuse the viewer's own unsent draft instead of generating another
      // (which voids it). A draft the other party created is still replaced:
      // only its creator can send it.
      const ownDraft = !existingIsSendable
        && purchaseAgreement
        && (purchaseAgreement.status || '').toLowerCase() === 'draft'
        && (purchaseAgreement.created_by_id ?? purchaseAgreement.created_by) === userId;
      let activeDocument = existingIsSendable || ownDraft
        ? await DocumentAPI.getDocument(purchaseAgreement.id)
        : await DocumentAPI.generateDocument(offerId, 'purchase_agreement');

      const documentOwnerId = activeDocument?.created_by_id ?? activeDocument?.created_by ?? null;
      const envelopeReady = ['sent', 'viewed', 'signed', 'completed'].includes((activeDocument?.status || '').toLowerCase())
        || Boolean(activeDocument?.docusign_envelope_id)
        || Boolean(activeDocument?.signatures?.length);

      if (!envelopeReady && documentOwnerId && documentOwnerId !== userId) {
        activeDocument = await DocumentAPI.generateDocument(offerId, 'purchase_agreement');
      }

      if (!envelopeReady && isWisconsin) {
        // Marital-property state: the sender confirms every required signer
        // (spouses included) and chooses how each spouse signs before the
        // envelope exists. The dialog's onSent opens the signing panel.
        setSendDialogDocId(activeDocument.id);
        return activeDocument;
      }

      if (!envelopeReady) {
        try {
          await sendDocumentEnvelope(activeDocument.id, {
            signers: [workspace?.buyer?.id, workspace?.seller?.id]
              .filter(Boolean)
              .map((signerId) => ({ signer_id: signerId, signing_mode: 'embedded' })),
          });
        } catch (sendErr) {
          // F1 backend race guard: when the server-side provisioning task
          // (F3, TX/AZ/CO allowlist) wins the race, /send returns 409
          // (DocumentAlreadySentError). That's a SUCCESS for us — the server
          // already produced an envelope on this document. Refetch and
          // continue; otherwise re-raise.
          if (sendErr?.status !== 409 && sendErr?.statusCode !== 409) {
            throw sendErr;
          }
        }
        activeDocument = await DocumentAPI.getDocument(activeDocument.id);
      }

      setActiveDocumentId(activeDocument.id);
      setDocStatus(activeDocument.status || 'sent');
      setShowDocPanel(true);
      await refreshDocumentStatus();
      return activeDocument;
    } catch (err) {
      console.error('Failed to provision negotiation envelope:', err);
      setDocProvisioningError(
        err?.message
          || 'The offer was accepted, but we could not open the signing envelope automatically. You can still launch it manually below.'
      );
      return null;
    } finally {
      setDocProvisioning(false);
    }
  }, [isWisconsin, offerId, refreshDocumentStatus, userId, workspace?.buyer?.id, workspace?.seller?.id]);

  // A saved confirmation can clear a send-readiness blocker, so re-check
  // document readiness.
  const handleMaritalAttestationSaved = useCallback(() => {
    refreshDocumentStatus();
  }, [refreshDocumentStatus]);

  const handleWisconsinEnvelopeSent = useCallback(async () => {
    const documentId = sendDialogDocId;
    setSendDialogDocId(null);
    if (!documentId) return;
    try {
      const sentDocument = await DocumentAPI.getDocument(documentId);
      setActiveDocumentId(sentDocument.id);
      setDocStatus(sentDocument.status || 'sent');
    } catch (err) {
      console.error('Failed to load the sent envelope:', err);
      setActiveDocumentId(documentId);
      setDocStatus('sent');
    }
    setShowDocPanel(true);
    await refreshDocumentStatus();
  }, [sendDialogDocId, refreshDocumentStatus]);

  // Only while no envelope exists yet: once one is in flight the confirmations
  // were already satisfied (the backend gated the send on them).
  const attestationNote =
    isWisconsin && !paInFlight
    && maritalAttestation?.required === true && !maritalAttestation.complete
      ? MARITAL_ATTESTATION_PENDING_NOTE
      : null;

  // F3/F4 bridge: when the negotiation status poll reports a new
  // `signature_state_hash`, any open DocumentFlowPanel already refetches its
  // own document (F4 wiring — see <DocumentFlowPanel signatureStateHash={...} />
  // below). But if NO doc panel is open (e.g., the user accepted the offer and
  // closed the auto-opened panel, then the server-side provisioning task
  // commits a fresh envelope), we still need to refresh local document
  // readiness so paReady / docStatus reflect reality. Only fires post-
  // acceptance to avoid spurious churn during pre-acceptance polling.
  const noPanelOpen = !showDocPanel && !showPaPanel;
  useEffect(() => {
    if (!signatureStateHash) return;
    if (!isAccepted) return;
    if (!noPanelOpen) return;
    refreshDocumentStatus();
  }, [signatureStateHash, isAccepted, noPanelOpen, refreshDocumentStatus]);

  const handleSubmitCounter = async (formData) => {
    setActionLoading(true);
    setCounterError(null);
    try {
      await submitCounter(offerId, formData);
      setShowCounterForm(false);
    } catch (err) {
      console.error('Counter-offer failed:', err);
      setCounterError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddComment = async (content) => {
    setActionLoading(true);
    try {
      await addComment(content);
      setShowCommentInput(false);
    } catch (err) {
      console.error('Failed to add comment:', err);
      // Let CommentInput keep the typed text and show an inline error.
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccept = async () => {
    setShowAcceptModal(true);
  };

  const handleSubmitAccept = async (offer) => {
    setActionLoading(true);
    setAcceptError(null);
    setDocProvisioningError(null);
    try {
      await acceptOffer(offerId);
      setShowAcceptModal(false);
      // Refresh workspace state so the new offer_status ('accepted') is
      // reflected before the envelope provisioning flow begins.
      try {
        await fetchTimeline();
      } catch (refreshErr) {
        console.warn('NegotiationWorkspace: workspace refresh after accept failed', refreshErr);
      }
      // No agreement to generate in a state the platform doesn't support yet;
      // the post-accept bar says so instead of surfacing a provisioning error.
      if (!documentsUnsupported) {
        await openEnvelopeWorkspace();
      }
    } catch (err) {
      console.error('Accept failed:', err);
      setAcceptError(err?.message || 'Failed to accept offer. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = () => {
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async () => {
    setRejectModalOpen(false);
    setActionLoading(true);
    try {
      await rejectOffer(offerId, rejectReason.trim());
    } catch (err) {
      console.error('Reject failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = () => {
    setShowWithdrawModal(true);
  };

  const handleWithdrawConfirm = async (reason, note) => {
    const combined = [reason, note].filter(Boolean).join(' — ');
    setActionLoading(true);
    try {
      await withdrawOffer(offerId, combined || 'Changed mind');
      setShowWithdrawModal(false);
    } catch (err) {
      console.error('Withdraw failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="negotiation-workspace">
        <button className="negotiation-back-btn" onClick={onBack}>← Back to Negotiations</button>
        <div className="negotiation-loading" role="status" aria-live="polite">
          <div className="negotiation-loading-spinner" aria-hidden="true" />
          <p>Loading negotiation workspace...</p>
        </div>
      </div>
    );
  }

  // No `buyerId` prop AND no authenticated user — the workspace is identity-
  // scoped (role derivation, signer matching, action permissions), so render
  // a clear auth-required cue instead of silently assuming a default user.
  if (!userId && !authLoading) {
    return (
      <div className="negotiation-workspace">
        <button className="negotiation-back-btn" onClick={onBack}>← Back to Negotiations</button>
        <div className="negotiation-empty" role="status" aria-live="polite">
          <div className="negotiation-empty-icon">🔒</div>
          <h3>Sign in to view your negotiation workspace</h3>
          <p>You need to be signed in to see negotiation details, timeline, and signing actions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="negotiation-workspace">
      <button className="negotiation-back-btn" onClick={onBack}>← Back to Negotiations</button>

      {error && (
        <div className="negotiation-error" role="alert">
          <p>{error}</p>
          <button onClick={() => fetchTimeline()} aria-label="Retry">↻</button>
        </div>
      )}

      {stale && !error && (
        <div
          className="negotiation-stale-banner"
          role="status"
          aria-live="polite"
          data-testid="negotiation-stale-banner"
        >
          <span aria-hidden="true">⚠</span>
          <span>Reconnecting… live updates are paused. Showing the last synced state.</span>
          <button className="negotiation-stale-retry" onClick={() => refresh()}>Retry now</button>
        </div>
      )}

      <DealProgressTracker
        offerStatus={workspace?.offer_status || workspace?.status}
        docStatus={docStatus}
      />

      <TransactionPhaseBar
        currentPhase={
          isAccepted && (docStatus === 'completed' || docStatus === 'signed')
            ? 'closing'
            : isAccepted
            ? 'documents'
            : 'negotiation'
        }
        completedPhases={
          isAccepted
            ? ['negotiation']
            : []
        }
      />

      <div className="negotiation-detail">
        {isAccepted && (
          <AcceptedCelebration
            price={terms.offer_price}
            propertyAddress={workspace?.property?.address}
            propertyState={propertyState}
            documentsSupported={documentsSupported}
            provisioningEnvelope={docProvisioning}
            agreementInFlight={paInFlight}
            sendBlockedNote={attestationNote}
            onGenerateDocuments={openEnvelopeWorkspace}
            onGoToDocuments={onNavigateToDocuments}
          />
        )}

        {isAccepted && isWisconsin && offerId && (
          <MaritalAttestationCard
            offerId={offerId}
            fallbackRole={myRole}
            onStatusChange={setMaritalAttestation}
            onSaved={handleMaritalAttestationSaved}
          />
        )}

        {/* Federal lead-paint disclosure answers (pre-1978 / unknown-year homes;
            renders nothing otherwise). A save can clear a send blocker and
            regenerates the disclosure, so re-check readiness. */}
        {isAccepted && offerId && (
          <LeadPaintDisclosureCard
            offerId={offerId}
            onSaved={handleMaritalAttestationSaved}
          />
        )}

        <NegotiationHeader workspace={workspace} myRole={myRole} />

        {isClosed && (
          <div className="negotiation-closed-banner" role="status" data-testid="negotiation-closed-banner">
            <p className="negotiation-closed-banner__text">
              {closedText || 'This offer was closed by cutthe6.com.'}
            </p>
            <p className="negotiation-closed-banner__hint">
              This offer is read-only. Documents you signed are still in Documents.
            </p>
            {typeof onNavigateToDocuments === 'function' && (
              <button
                type="button"
                className="negotiation-closed-banner__btn"
                onClick={onNavigateToDocuments}
                data-testid="negotiation-closed-documents"
              >
                View my documents
              </button>
            )}
          </div>
        )}

        <div className="negotiation-body">
          <div className="negotiation-main">
            {/* Turn cue — the single, prominent "whose move is it" signal.
                Replaces the AI mediator button as the top-of-workflow guidance
                and surfaces what used to live only in button tooltips. */}
            {!isTerminal && !isAccepted && activeTurn && (
              <div
                className={`turn-banner${isMyTurn ? ' turn-banner--you' : ''}`}
                role="status"
                aria-live="polite"
                data-testid="turn-banner"
              >
                <span className="turn-banner-icon" aria-hidden="true">{isMyTurn ? '🎯' : '⏳'}</span>
                <div className="turn-banner-body">
                  <div className="turn-banner-title">
                    {isMyTurn ? 'Your move' : `Waiting on the ${activeTurn}`}
                  </div>
                  <div className="turn-banner-desc">
                    {isMyTurn
                      ? 'Review the latest terms below, then accept, counter, or respond.'
                      : `We'll update this page automatically when the ${activeTurn} responds. You can still add a comment in the meantime.`}
                  </div>
                </div>
              </div>
            )}

            {/* Live terms snapshot — pins the current price and key terms to
                the top of the column so the decision (accept / counter / reject)
                never requires scrolling back to find the latest numbers. */}
            {!isTerminal && <CurrentTermsSummary terms={terms} />}

            <NegotiationTimeline timeline={timeline} buyerId={userId} />

            {/* Action bar — only show if not terminal */}
            {!isTerminal && (
              <div className="negotiation-actions">
                {/* Purchase Agreement prep is optional pre-acceptance */}
                {documentsSupported && !isAccepted && (
                  <div className={`recr-banner${paReady ? ' recr-banner--done' : paReady === false ? ' recr-banner--pending' : ' recr-banner--loading'}`}>
                    <div className="recr-banner-icon">
                      {paReady === null ? '⏳' : paReady ? '✅' : '📝'}
                    </div>
                    <div className="recr-banner-body">
                      <div className="recr-banner-title">
                        Purchase Agreement — Draft Before Acceptance
                      </div>
                      <div className="recr-banner-desc">
                        {paReady === null
                          ? 'Checking document status…'
                          : paReady
                            ? 'Purchase Agreement is already completed. You can still proceed with acceptance workflow.'
                            : paStatusCheckError
                              ? 'Could not confirm document status right now. You can draft the Purchase Agreement now for review — it becomes binding only after acceptance and signing.'
                              : 'You can draft the Purchase Agreement now for review. It becomes binding only after acceptance and signing.'}
                      </div>
                    </div>
                    {!paReady && (
                      <button
                        className="recr-banner-cta"
                        onClick={() => setShowPaPanel(true)}
                        disabled={paReady === null}
                      >
                        Draft Purchase Agreement
                      </button>
                    )}
                  </div>
                )}
                {/* WI seller disclosure status. The report is completed and signed
                    on the listing, never generated here. */}
                {isWisconsin && (
                  <div className={`recr-banner${recrCompleted ? ' recr-banner--done' : recrCompleted === false ? ' recr-banner--pending' : ' recr-banner--loading'}`}>
                    <div className="recr-banner-icon">
                      {recrCompleted === null ? '⏳' : recrCompleted ? '✅' : '📋'}
                    </div>
                    <div className="recr-banner-body">
                      <div className="recr-banner-title">
                        {DISCLOSURE_COPY.title({ title: disclosureTitle })}
                      </div>
                      <p className="recr-banner-info" data-testid="recr-banner-info">
                        {DISCLOSURE_COPY.info[disclosureBasis]}
                      </p>
                      <div className="recr-banner-desc">
                        {recrCompleted === null
                          ? 'Checking disclosure status…'
                          : recrCompleted
                            // recrCompleted is also true for a report completed before
                            // owners signed it on the listing — don't claim a signature.
                            ? 'Seller disclosure completed.'
                            : isSeller
                              ? 'The report is completed and signed on your listing, not in this workspace. Acceptance is still available.'
                              : 'Seller disclosure is pending. Acceptance is still available.'}
                      </div>
                      {isSeller && recrCompleted === false && <RecrListingGuide />}
                    </div>
                    {recrCompleted && (
                      <span className="recr-banner-badge">Completed</span>
                    )}
                  </div>
                )}
                {documentsUnsupported && (
                  <p className="ws-under-contract-note" role="note" data-testid="ws-docs-unsupported-note">
                    {docsUnsupportedText(propertyState)}
                  </p>
                )}
                {propertyUnderContract && (
                  <p className="ws-under-contract-note" role="note" data-testid="ws-under-contract-note">
                    {PROPERTY_UNDER_CONTRACT_TEXT}
                  </p>
                )}
                {/* Constructive moves (keep the conversation going) sit together;
                    the terminal move (reject / withdraw) is split off into its own
                    group so it can't be fat-fingered in place of Accept. */}
                <div className="action-group action-group--primary" data-testid="action-group-primary">
                  <button
                    className="action-btn action-btn-counter"
                    onClick={() => { setShowCounterForm(!showCounterForm); setShowCommentInput(false); }}
                    disabled={actionLoading || !canCounter}
                    title={!canCounter ? `Waiting for the ${activeTurn || 'other party'} to respond` : undefined}
                  >
                    💰 Counter Offer
                  </button>
                  <button
                    className="action-btn action-btn-comment"
                    onClick={() => { setShowCommentInput(!showCommentInput); setShowCounterForm(false); }}
                    disabled={actionLoading}
                  >
                    💬 Add Comment
                  </button>
                  <button
                    className="action-btn action-btn-accept"
                    onClick={handleAccept}
                    disabled={actionLoading || !canAccept}
                    title={
                      propertyUnderContract
                        ? PROPERTY_UNDER_CONTRACT_TEXT
                        : !isMyTurn
                          ? `Waiting for the ${activeTurn || 'other party'} to respond`
                          : undefined
                    }
                  >
                    ✓ Accept
                  </button>
                </div>
                {(isSeller || isBuyer) && (
                  <div className="action-group action-group--terminal" data-testid="action-group-terminal">
                    {isSeller && (
                      <button
                        className="action-btn action-btn-reject"
                        onClick={handleReject}
                        disabled={actionLoading || !canReject}
                        title={!canReject ? "You can only reject when it's your turn to respond" : undefined}
                      >
                        ✕ Reject
                      </button>
                    )}
                    {isBuyer && (
                      <button
                        className="action-btn action-btn-withdraw"
                        onClick={handleWithdraw}
                        disabled={actionLoading || !canWithdraw}
                        title={!canWithdraw ? "You can only withdraw your own pending offer or counter" : undefined}
                      >
                        ↩ Withdraw
                      </button>
                    )}
                  </div>
                )}
                {actionsDisabledReason && (
                  <p
                    className="action-disabled-reason"
                    role="note"
                    data-testid="action-disabled-reason"
                  >
                    {actionsDisabledReason}
                  </p>
                )}
              </div>
            )}

            {/* Post-acceptance action bar */}
            {isAccepted && !showDocPanel && (
              <div className="negotiation-actions post-accept-actions">
                {/* WI seller disclosure — shown post-acceptance until completed */}
                {isWisconsin && !recrCompleted && (
                  <div className={`recr-banner${recrCompleted === false ? ' recr-banner--pending' : ' recr-banner--loading'}`}>
                    <div className="recr-banner-icon">
                      {recrCompleted === null ? '⏳' : '📋'}
                    </div>
                    <div className="recr-banner-body">
                      <div className="recr-banner-title">
                        {DISCLOSURE_COPY.postAcceptTitle[disclosureBasis]({ title: disclosureTitle })}
                      </div>
                      <div className="recr-banner-desc">
                        {recrCompleted === null
                          ? 'Checking disclosure status…'
                          : isSeller
                            ? DISCLOSURE_COPY.postAcceptSeller
                            : 'Waiting for seller to complete the Seller Disclosure.'}
                      </div>
                      {isSeller && recrCompleted === false && <RecrListingGuide />}
                    </div>
                  </div>
                )}
                {docProvisioningError && (
                  <div className="negotiation-error" role="alert">
                    <p>{docProvisioningError}</p>
                    <button onClick={openEnvelopeWorkspace} aria-label="Retry agreement envelope setup">↻</button>
                  </div>
                )}
                {documentsUnsupported && (
                  <p className="ws-under-contract-note" role="note" data-testid="ws-docs-unsupported-note">
                    {docsUnsupportedText(propertyState, true)}
                  </p>
                )}
                <button
                  className="action-btn action-btn-doc"
                  onClick={openEnvelopeWorkspace}
                  disabled={docProvisioning || documentsUnsupported}
                >
                  {docProvisioning
                    ? '⏳ Preparing envelope…'
                    : paInFlight
                    ? '✍️ Review & Sign Documents'
                    : '📝 Generate & Sign Documents'}
                </button>
                {typeof onNavigateToDocuments === 'function' && (
                  <button
                    className="action-btn action-btn-comment"
                    onClick={onNavigateToDocuments}
                  >
                    📋 View All Documents
                  </button>
                )}
                {attestationNote && (
                  <p className="post-accept-attestation-note" data-testid="post-accept-attestation-note">
                    {attestationNote}
                  </p>
                )}
              </div>
            )}

            {/* Counter-offer form */}
            {showCounterForm && !isTerminal && (
              <CounterOfferForm
                property={counterProperty}
                myRole={myRole}
                currentTerms={terms}
                currentPrice={terms.offer_price}
                listPrice={workspace?.property?.price}
                onSubmit={handleSubmitCounter}
                onCancel={() => { setShowCounterForm(false); setCounterError(null); }}
                loading={actionLoading}
                submitError={counterError}
              />
            )}

            {/* Comment input */}
            {showCommentInput && !isTerminal && (
              <CommentInput
                onSubmit={handleAddComment}
                onCancel={() => setShowCommentInput(false)}
                loading={actionLoading}
              />
            )}

            {/* Document Gate — shown while offer is pending/countered */}
            {!isAccepted && !isTerminal && (offerStatus === 'pending' || offerStatus === 'countered') && (
              <DocumentGateSection
                offerStatus={offerStatus}
                propertyAddress={workspace?.property?.address}
              />
            )}

            {/* Inline Purchase Agreement Panel — pre-acceptance draft */}
            {showPaPanel && offerId && (
              <DocumentFlowPanel
                offerId={offerId}
                offerStatus={workspace?.offer_status || offerStatus}
                propertyAddress={workspace?.property?.address}
                mode="inline"
                autoStart={true}
                templateOptions={[{ value: 'purchase_agreement', label: 'Purchase Agreement' }]}
                defaultTemplate="purchase_agreement"
                signatureStateHash={signatureStateHash}
                onClose={() => {
                  setShowPaPanel(false);
                  refreshDocumentStatus();
                }}
                onComplete={() => {
                  setShowPaPanel(false);
                  setPaReady(true);
                  refreshDocumentStatus();
                }}
              />
            )}

            {sendDialogDocId && (
              <SendDocumentDialog
                open
                documentId={sendDialogDocId}
                documentType="Purchase Agreement"
                propertyAddress={workspace?.property?.address}
                propertyState={propertyState}
                onClose={() => setSendDialogDocId(null)}
                onSent={handleWisconsinEnvelopeSent}
                onRegenerated={handleMaritalAttestationSaved}
              />
            )}

            {/* Inline Document Panel */}
            {showDocPanel && offerId && (
              <DocumentFlowPanel
                offerId={offerId}
                offerStatus={workspace?.offer_status || offerStatus}
                propertyAddress={workspace?.property?.address}
                initialDocumentId={activeDocumentId}
                mode="inline"
                signatureStateHash={signatureStateHash}
                onClose={() => {
                  setShowDocPanel(false);
                  refreshDocumentStatus();
                }}
                onComplete={() => {
                  setDocStatus('signed');
                  refreshDocumentStatus();
                }}
              />
            )}

            {/* Negotiation Flow Diagram — at bottom of workflow */}
            <NegotiationFlowDiagram
              workspace={workspace}
              timeline={timeline}
              isWisconsin={isWisconsin}
              recrCompleted={recrCompleted}
              myRole={myRole}
            />
          </div>
        </div>
      </div>

            {/* Withdraw Offer Modal */}
      <AcceptOfferModal
        open={showAcceptModal}
        offer={workspace ? { ...workspace.current_terms, property_address: workspace.property?.address, property_id: workspace.property?.id, property_timezone: workspace.property?.timezone } : null}
        currentRole={myRole}
        documentsUnsupportedState={documentsUnsupported ? propertyState : null}
        submitting={actionLoading}
        onSubmit={handleSubmitAccept}
        error={acceptError}
        onClose={() => {
          setShowAcceptModal(false);
          setAcceptError(null);
        }}
      />

      {/* Withdraw Offer Modal */}
      <WithdrawConfirmModal
        open={showWithdrawModal}
        loading={actionLoading}
        onConfirm={handleWithdrawConfirm}
        onCancel={() => setShowWithdrawModal(false)}
      />

      {/* Reject Offer Modal */}
      {rejectModalOpen && (
        <div className="withdraw-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="reject-modal-title">
          <div className="withdraw-modal" ref={rejectDialogRef} tabIndex={-1}>
            <div className="withdraw-modal-header">
              <span className="withdraw-modal-icon">✕</span>
              <h2 id="reject-modal-title">Reject This Offer</h2>
              <button
                className="withdraw-modal-close"
                onClick={() => setRejectModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="withdraw-modal-body">
              <div className="withdraw-modal-field">
                <label htmlFor="reject-reason-input" className="withdraw-modal-label">
                  Reason for rejection <span className="withdraw-modal-optional">(optional)</span>
                </label>
                <textarea
                  id="reject-reason-input"
                  className="withdraw-modal-textarea"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  maxLength={300}
                  rows={3}
                  placeholder="e.g. The offer price does not meet our expectations at this time."
                />
                <span className="withdraw-modal-char-count">{rejectReason.length}/300</span>
              </div>
            </div>
            <div className="withdraw-modal-footer">
              <button
                className="withdraw-modal-btn-cancel"
                onClick={() => setRejectModalOpen(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="withdraw-modal-btn-confirm"
                onClick={handleRejectConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? 'Rejecting…' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// WORKSPACE LIST VIEW
// ============================================================

const WorkspaceList = ({
  onSelect,
  buyerId,
  userRole,
  initialOfferId,
  initialPropertyId,
  initialPropertyAddress,
  onInitialTargetHandled,
}) => {
  // Identity resolution: prop wins, then auth, then null. No silent fallback
  // to user 1 — when both are missing we render an auth-required cue below
  // and skip the API request entirely.
  const { user: currentUser, loading: authLoading } = useAuth();
  const currentUserId = currentUser?.id ?? null;
  const resolvedUserId = buyerId ?? currentUserId ?? null;

  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addressFilter, setAddressFilter] = useState(initialPropertyAddress || '');
  const [currentPage, setCurrentPage] = useState(1);
  const autoAttemptedRef = useRef(false);

  useEffect(() => {
    setAddressFilter(initialPropertyAddress || '');
  }, [initialPropertyAddress]);

  const fetchWorkspaces = useCallback(async () => {
    if (!resolvedUserId) {
      // Auth still resolving or no user — leave list empty and let the
      // auth-required empty state below render. Don't ping the API with a
      // bogus default id.
      setWorkspaces([]);
      setLoading(authLoading);
      return;
    }
    setLoading(true);
    try {
      const data = await NegotiationAPI.getMyWorkspaces(resolvedUserId);
      setWorkspaces(data.workspaces || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [resolvedUserId, authLoading]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const sortedFilteredWorkspaces = useMemo(() => {
    const needle = (addressFilter || '').trim().toLowerCase();
    const filtered = needle
      ? workspaces.filter((ws) => (ws.property_address || '').toLowerCase().includes(needle))
      : workspaces;

    return [...filtered].sort((a, b) => {
      const aTime = new Date(a?.last_activity_at || 0).getTime();
      const bTime = new Date(b?.last_activity_at || 0).getTime();
      return bTime - aTime;
    });
  }, [workspaces, addressFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [addressFilter, workspaces]);

  const totalPages = Math.max(1, Math.ceil(sortedFilteredWorkspaces.length / WORKSPACES_PER_PAGE));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const pageStart = (currentPageSafe - 1) * WORKSPACES_PER_PAGE;
  const pageEnd = pageStart + WORKSPACES_PER_PAGE;
  const paginatedWorkspaces = sortedFilteredWorkspaces.slice(pageStart, pageEnd);

  useEffect(() => {
    const hasInitialTarget = Boolean(initialOfferId || initialPropertyId || initialPropertyAddress);
    if (!hasInitialTarget || autoAttemptedRef.current || loading) return;

    autoAttemptedRef.current = true;

    const normalizedAddress = (initialPropertyAddress || '').trim().toLowerCase();
    // Each workspace belongs to one offer, so the offer id is exact; a listing
    // can have several workspaces (one per buyer offer).
    const offerMatch = initialOfferId != null
      ? workspaces.find((ws) => ws.offer_id != null && Number(ws.offer_id) === Number(initialOfferId))
      : null;
    const match = offerMatch || workspaces.find((ws) => {
      const wsPropertyId = ws.property_id ?? ws.property?.id ?? null;
      const idMatch = initialPropertyId != null && wsPropertyId != null && Number(wsPropertyId) === Number(initialPropertyId);
      const wsAddress = (ws.property_address || '').trim().toLowerCase();
      // The offer carries the full address ("707 Walnut Drive, Houston, TX
      // 77002"); the workspace list only the street line.
      const addressMatch = normalizedAddress && wsAddress
        && (normalizedAddress.includes(wsAddress) || wsAddress.includes(normalizedAddress));
      return idMatch || addressMatch;
    });

    if (match?.id) {
      onSelect(match.id);
    }

    if (typeof onInitialTargetHandled === 'function') {
      onInitialTargetHandled();
    }
  }, [
    loading,
    workspaces,
    onSelect,
    initialOfferId,
    initialPropertyId,
    initialPropertyAddress,
    onInitialTargetHandled,
  ]);

  if (loading || authLoading) {
    return (
      <div className="negotiation-workspace">
        <h1 className="negotiation-page-title">Negotiations</h1>
        <div className="negotiation-loading" role="status" aria-live="polite">
          <div className="negotiation-loading-spinner" aria-hidden="true" />
          <p>Loading your negotiations...</p>
        </div>
      </div>
    );
  }

  // No `buyerId` prop AND no authenticated user — show a clear auth-required
  // cue instead of silently presenting an empty list as if the user were
  // signed in.
  if (!resolvedUserId) {
    return (
      <div className="negotiation-workspace">
        <h1 className="negotiation-page-title">Negotiations</h1>
        <div className="negotiation-empty" role="status" aria-live="polite">
          <div className="negotiation-empty-icon">🔒</div>
          <h3>Sign in to view your negotiations</h3>
          <p>Your negotiation workspaces appear here once you&apos;re signed in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="negotiation-workspace">
      <h1 className="negotiation-page-title">Negotiations</h1>
      <p className="negotiation-page-subtitle">Your active and past negotiation workspaces</p>

      <div className="workspace-filter-wrapper">
        <input
          type="text"
          value={addressFilter}
          onChange={(e) => setAddressFilter(e.target.value)}
          placeholder="Filter by property address"
          aria-label="Filter negotiations by property address"
          className="workspace-filter-input"
        />
      </div>

      {error && (
        <div className="negotiation-error" role="alert">
          <p>{error}</p>
          <button onClick={fetchWorkspaces} aria-label="Retry">↻</button>
        </div>
      )}

      {sortedFilteredWorkspaces.length === 0 && !error ? (
        <div className="negotiation-empty">
          <div className="negotiation-empty-icon">🤝</div>
          <h3>{workspaces.length === 0 ? 'No negotiations yet' : 'No matching negotiations'}</h3>
          <p>
            {workspaces.length === 0
              ? 'When you submit an offer on a property, a negotiation workspace will be created automatically.'
              : 'Try a different address filter or clear the filter to see all negotiations.'}
          </p>
        </div>
      ) : (
        <>
          <div className="negotiation-list-meta">
            <span>
              Showing {paginatedWorkspaces.length} of {sortedFilteredWorkspaces.length} negotiation{sortedFilteredWorkspaces.length === 1 ? '' : 's'}
            </span>
            <span className="negotiation-list-sort">Sorted: Newest → Oldest</span>
          </div>
          <div className="workspace-list">
            {paginatedWorkspaces.map((ws) => (
              <button
                key={ws.id}
                className="workspace-card"
                onClick={() => onSelect(ws.id)}
                aria-label={`Open negotiation ${ws.id}`}
              >
                <div className="workspace-card-header">
                  <h3 className="workspace-card-property">
                    {ws.property_address || `Workspace #${ws.id}`}
                  </h3>
                  <StatusBadge status={ws.offer_status} />
                </div>
                <div className="workspace-card-meta">
                  <div className="workspace-card-row">
                    <span>{formatTime(ws.last_activity_at)}</span>
                  </div>
                </div>
                <div className="workspace-card-parties">
                  <div className="workspace-card-party">
                    <span className="party-dot buyer" />
                    <span>{userRole === 'seller' ? (ws.counterparty_name || 'Buyer') : 'You (Buyer)'}</span>
                  </div>
                  <div className="workspace-card-party">
                    <span className="party-dot seller" />
                    <span>{userRole === 'seller' ? 'You (Seller)' : (ws.counterparty_name || 'Seller')}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="negotiation-pagination" role="navigation" aria-label="Negotiations pagination">
              <button
                type="button"
                className="negotiation-pagination-btn"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPageSafe === 1}
              >
                ← Previous
              </button>
              <span className="negotiation-pagination-info">
                Page {currentPageSafe} of {totalPages}
              </span>
              <button
                type="button"
                className="negotiation-pagination-btn"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPageSafe === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

/**
 * NegotiationWorkspace — Main container.
 * Shows list view by default, detail view when a workspace is selected.
 */
const NegotiationWorkspace = ({
  buyerId,
  userRole,
  onNavigateToDocuments,
  initialOfferId,
  initialPropertyId,
  initialPropertyAddress,
  onInitialTargetHandled,
}) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);

  if (selectedWorkspaceId) {
    return (
      <WorkspaceDetail
        workspaceId={selectedWorkspaceId}
        onBack={() => setSelectedWorkspaceId(null)}
        buyerId={buyerId}
        userRole={userRole}
        onNavigateToDocuments={onNavigateToDocuments}
      />
    );
  }

  return (
    <WorkspaceList
      onSelect={setSelectedWorkspaceId}
      buyerId={buyerId}
      userRole={userRole}
      initialOfferId={initialOfferId}
      initialPropertyId={initialPropertyId}
      initialPropertyAddress={initialPropertyAddress}
      onInitialTargetHandled={onInitialTargetHandled}
    />
  );
};

export default NegotiationWorkspace;
